import React, { useState, useEffect, useRef } from "react";
import axios from 'axios';
import "react-datepicker/dist/react-datepicker.css";
import { Country, State, City } from "country-state-city";
import { Plus, Upload, X, CheckCircle, Trash, Edit, FilePlus, MapPinned, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TextField, MenuItem, Button, Box, Typography, Grid, Paper, Divider, IconButton, FormControl, InputLabel, Select, Checkbox, FormControlLabel, FormGroup } from '@mui/material';

import axiosInstance from "../axiosInstance";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddProject1 = ({ editingProject, onClose, handleReloadPrjects }) => {
  const [formData, setFormData] = useState({
    project_name: "",
    sector: "",
    cost: "",
    status: "",
    country: "",
    state: "",
    city: "",
    startDate: null,
    endDate: null,
    contractor: "",
    districtMagistrate: '',
    population: '',
    registrarOffice: '',
    area: '',
    areaUnit: 'sqft',
    circleRate: '',
    circleRateUnit: 'sqft',
    kmlFile: null,
    documentFile: null,
    transportation_access: false,
    nearest_infra: false,
    bankingPartner: false,
    job_impact: false,
    population_projection: false,
    land_price: false,
    social_impact: false
  });

  const [sectors, setSectors] = useState(["Commercial", "Construction", "Education", "Factory", "Highways", "Infrastructure", "Mall", "Railways", "Residential", "Retail"]);
  const [newSector, setNewSector] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [shakeFields, setShakeFields] = useState([]);
  const [sectorError, setSectorError] = useState("");
  const [fileName, setFileName] = useState('');
  const [kmlFileName, setKmlFileName] = useState('');
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [kmlFiles, setKmlFiles] = useState([]);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const [errorFields, setErrorFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(true); // New state to control form visibility

  const companyId = '67ce66294f28f85e4ff91e2c';
  const navigate = useNavigate();
  const formRef = useRef(null);

  useEffect(() => {
    if (editingProject) {
      setFormData({
        ...editingProject,
        startDate: editingProject.startDate ? new Date(editingProject.startDate) : null,
        endDate: editingProject.endDate ? new Date(editingProject.endDate) : null,
        areaUnit: editingProject.areaUnit || 'sqft',
        circleRateUnit: editingProject.circleRateUnit || 'sqft',
        transportation_access: editingProject.transportation_access || false,
        nearest_infra: editingProject.nearest_infra || false,
        bankingPartner: editingProject.bankingPartner || false,
        job_impact: editingProject.job_impact || false,
        population_projection: editingProject.population_projection || false,
        land_price: editingProject.land_price || false,
        social_impact: editingProject.social_impact || false
      });
      if (editingProject.kmlFile) {
        setKmlFileName(editingProject.kmlFile.name || 'KML file uploaded');
      }
    }
  }, [editingProject]);

  useEffect(() => {
    if (errorFields.length > 0) {
      const timer = setTimeout(() => {
        setErrorFields([]);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [errorFields]);

  const handleAddSector = () => {
    if (newSector.trim() && !sectors.includes(newSector.trim())) {
      setSectors((prev) => [...prev, newSector.trim()]);
      setNewSector("");
      setShowModal(false);
      setSectorError("");
    } else {
      setSectorError("Sector already exists or invalid input!");
      setShakeFields(["sector"]);
    }
  };

  const handleDeleteSector = (sectorToDelete) => {
    setSectors((prev) => prev.filter((sector) => sector !== sectorToDelete));
    if (formData.sector === sectorToDelete) {
      setFormData({ ...formData, sector: "" });
    }
  };

  const getCountryCode = (countryName) => {
    const country = Country.getAllCountries().find((c) => c.name === countryName);
    return country ? country.isoCode : "";
  };

  const getStateCode = (stateName, countryCode) => {
    const state = State.getStatesOfCountry(countryCode).find((s) => s.name === stateName);
    return state ? state.isoCode : "";
  };

  const [document, setDocument] = useState("");

  const handleChange = (e) => {
    const { name, value, files, checked, type } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else if (name === "kmlFile") {
      const selectedFiles = Array.from(files);
      setKmlFiles(selectedFiles);
      setFormData({
        ...formData,
        [name]: selectedFiles[0],
      });
      if (selectedFiles.length > 0) {
        setKmlFileName(
          selectedFiles.length > 1
            ? `${selectedFiles.length} files selected`
            : selectedFiles[0].name
        );
      } else {
        setKmlFileName('');
      }
    } else if (name === "documentFile") {
      const file = files[0];
      setDocument(file)
      setFormData({
        ...formData,
        [name]: file,
      });
      setFileName(file ? file.name : '');
    } else if (name === "country") {
      setFormData({
        ...formData,
        [name]: value,
        state: "",
        city: "",
      });
    } else if (name === "state") {
      setFormData({
        ...formData,
        [name]: value,
        city: "",
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDateChange = (date, field) => {
    setFormData({
      ...formData,
      [field]: date,
    });
  };

  const validateForm = () => {
    const requiredFields = [
      'project_name',
      'sector',
      'cost',
      'status',
      'country',
      'state',
      'city'
    ];
    
    const emptyFields = requiredFields.filter(field => !formData[field]);
    
    if (emptyFields.length > 0 || (!formData.kmlFile && !formData.documentFile)) {
      setShakeFields(emptyFields);
      setErrorFields(emptyFields);
      setShowRequiredError(true);
      
      setTimeout(() => {
        setShowRequiredError(false);
        setErrorFields([]);
      }, 2000);
      
      return false;
    }
    
    setShowRequiredError(false);
    return true;
  };

  const handleSubmit = async (payloadurl) => {
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Create the payload structure that matches backend expectations
      const payload = {
        project_name: formData.project_name,
        sector: formData.sector,
        cost: formData.cost,
        status: formData.status,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        startDate: formData.startDate?.toISOString().split("T")[0],
        endDate: formData.endDate?.toISOString().split("T")[0],
        contractor: formData.contractor,
        districtMagistrate: formData.districtMagistrate,
        population: formData.population,
        registrarOffice: formData.registrarOffice,
        area: formData.area,
        areaUnit: formData.areaUnit,
        circleRate: formData.circleRate,
        circleRateUnit: formData.circleRateUnit,
        transportation_access: formData.transportation_access,
        nearest_infra: formData.nearest_infra,
        job_impact: formData.job_impact,
        population_projection: formData.population_projection,
        land_price: formData.land_price,
        social_impact: formData.social_impact,
        kml: payloadurl.kmlUrls ? payloadurl.kmlUrls.map(url => ({ url })) : [],
        documentFile: payloadurl.documentUrl ? payloadurl.documentUrl : null,
        district: {
          registrarOffice: formData.registrarOffice,
          circleRate: formData.circleRate,
          population: formData.population,
          districtMagistrate: formData.districtMagistrate,
          circleRateUnit: formData.circleRateUnit
        }
      };

      if (editingProject) {
        const response = await axiosInstance.put(
          `/project/updateProject`,
          payload,
          {
            headers: {
              'x-company-id': companyId,
              'Content-Type': 'application/json',
              'x-project-id': editingProject._id
            },
          }
        );
        setShowForm(false); // Hide the form
        setShowUpdateModal(true);
      } else {
        const response = await axiosInstance.post(
          '/project/createProject',
          payload,
          {
            headers: {
              'x-company-id': companyId,
              'x-auth-token': localStorage.getItem('token'),
              'Content-Type': 'application/json',
            },
          }
        );
        setShowForm(false); // Hide the form
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error submitting project:', error.response?.data || error.message);
      toast.error('Error submitting project');
    } finally {
      handleReloadPrjects()
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        closeSuccessModal();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    if (showUpdateModal) {
      const timer = setTimeout(() => {
        closeUpdateModal();
      }, 2000);
  
      return () => clearTimeout(timer);
    }
  }, [showUpdateModal]);
       
  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    onClose();
  };

  const shouldShake = (fieldName) => {
    return shakeFields.includes(fieldName) ? "animate-shake" : "";
  };

  const handleKmlUpload = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }
    
    if (kmlFiles.length === 0 && !document) {
      setMessage("No files selected!");
      await handleSubmit({});
      return;
    }

    setIsUploading(true);
    setMessage("Uploading files...");

    try {
      let uploadedKmlUrls = [];
      let documentUrl = null;

      if (kmlFiles.length > 0) {
        for (const file of kmlFiles) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("upload_preset", "ml_default");

          const response = await axios.post(
            "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
            formData
          );
          uploadedKmlUrls.push(response.data.secure_url);
        }
      }

      if (document) {
        const formData = new FormData();
        formData.append("file", document);
        formData.append("upload_preset", "ml_default");

        const response = await axios.post(
          "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
          formData
        );
        documentUrl = response.data.secure_url;
      }

      const payload = {
        kmlUrls: uploadedKmlUrls,
        documentUrl: documentUrl
      };

      await handleSubmit(payload);
    } catch (error) {
      toast.error("Upload failed");
      console.error("Upload Error:", error);
    } finally {
      setIsUploading(false);
      setKmlFiles([]);
    }
  };

  const renderLabel = (fieldName, label) => {
    const requiredFields = ['project_name', 'sector', 'cost', 'status', 'country', 'state', 'city'];
    const isRequired = requiredFields.includes(fieldName);
    const isMissing = errorFields.includes(fieldName);
    
    return (
      <span>
        {label}
        {isRequired && <span style={{ 
          color: isMissing ? 'red' : 'inherit',
          transition: 'color 0.5s ease-out'
        }}>*</span>}
      </span>
    );
  };

  const handlePlusButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" p={1} ref={formRef}>
      <ToastContainer position="top-right" autoClose={5000} />
      <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2, maxWidth: 700, width: '100%', background: '#fff', mt: 0, position: 'relative' }}>
        <button
          className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
          onClick={onClose}
        >
          <X size={14} />
        </button>

        <Typography variant="h6" align="center" fontWeight="bold" color="primary" gutterBottom sx={{ mb: 1 }}>
          {editingProject ? "Edit Project" : "Add Project"}
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        <form onSubmit={(e) => handleKmlUpload(e)} autoComplete="off">
          <Grid container spacing={1}>
            {/* Project Name */}
            <Grid item xs={12} md={6}>
              <TextField
                label={renderLabel("project_name", "Project Name")}
                fullWidth
                size="small"
                name="project_name"
                value={formData.project_name}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                className={shouldShake("project_name")}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errorFields.includes("project_name") ? 'red' : '',
                      transition: 'border-color 0.5s ease-out'
                    },
                  },
                }}
              />
            </Grid>

            {/* Sector */}
            <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
              <TextField
                label={renderLabel("sector", "Sector")}
                select
                fullWidth
                size="small"
                name="sector"
                value={formData.sector}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                className={shouldShake("sector")}
                SelectProps={{
                  renderValue: (selected) => selected,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errorFields.includes("sector") ? 'red' : '',
                      transition: 'border-color 0.5s ease-out'
                    },
                  },
                }}
              >
                <MenuItem value="">Select Sector</MenuItem>
                {sectors.map((sector, index) => (
                  <MenuItem key={index} value={sector}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      {sector}
                      {formData.sector !== sector && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSector(sector);
                          }}
                          size="small"
                        >
                          <X size={14} />
                        </IconButton>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            
              <button
                className="absolute top-1 -right-3 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
                onClick={handlePlusButtonClick}
              >
                <Plus size={18} />
              </button>
            </Grid>

            {/* Cost */}
            <Grid item xs={12} md={6}>
              <TextField
                label={renderLabel("cost", "Cost (Cr)")}
                type="number"
                fullWidth
                size="small"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                className={shouldShake("cost")}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errorFields.includes("cost") ? 'red' : '',
                      transition: 'border-color 0.5s ease-out'
                    },
                  },
                }}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <TextField
                label={renderLabel("status", "Status")}
                select
                fullWidth
                size="small"
                name="status"
                value={formData.status}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                className={shouldShake("status")}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errorFields.includes("status") ? 'red' : '',
                      transition: 'border-color 0.5s ease-out'
                    },
                  },
                }}
              >
                <MenuItem value="">Select Status</MenuItem>
                <MenuItem value="Conceptual">Conceptual</MenuItem>
                <MenuItem value="DPR">DPR</MenuItem>
                <MenuItem value="Tender">Tender</MenuItem>
                <MenuItem value="Under-Construction">Under-Construction</MenuItem>
                <MenuItem value="Constructed">Constructed</MenuItem>
              </TextField>
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <TextField
                label={renderLabel("country", "Country")}
                select
                fullWidth
                size="small"
                name="country"
                value={formData.country}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                className={shouldShake("country")}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errorFields.includes("country") ? 'red' : '',
                      transition: 'border-color 0.5s ease-out'
                    },
                  },
                }}
              >
                <MenuItem value="">Select Country</MenuItem>
                {Country.getAllCountries().map((country) => (
                  <MenuItem key={country.isoCode} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* State */}
            <Grid item xs={12} md={6}>
              <TextField
                label={renderLabel("state", "State")}
                select
                fullWidth
                size="small"
                name="state"
                value={formData.state}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                className={shouldShake("state")}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errorFields.includes("state") ? 'red' : '',
                      transition: 'border-color 0.5s ease-out'
                    },
                  },
                }}
              >
                <MenuItem value="">Select State</MenuItem>
                {State.getStatesOfCountry(getCountryCode(formData.country)).map((state) => (
                  <MenuItem key={state.isoCode} value={state.name}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* City */}
            <Grid item xs={12} md={6}>
              <TextField
                label={renderLabel("city", "City")}
                select
                fullWidth
                size="small"
                name="city"
                value={formData.city}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                className={shouldShake("city")}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: errorFields.includes("city") ? 'red' : '',
                      transition: 'border-color 0.5s ease-out'
                    },
                  },
                }}
              >
                <MenuItem value="">Select City</MenuItem>
                {City.getCitiesOfState(getCountryCode(formData.country), getStateCode(formData.state, getCountryCode(formData.country))).map((city) => (
                  <MenuItem key={city.name} value={city.name}>
                    {city.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Contractor */}
            <Grid item xs={12} md={6}>
              <TextField 
                label="Contractor" 
                fullWidth 
                size="small" 
                name="contractor" 
                value={formData.contractor} 
                onChange={handleChange} 
                variant="outlined" 
                margin="dense" 
              />
            </Grid>

            {/* District Magistrate */}
            <Grid item xs={12} md={6}>
              <TextField
                label="District Magistrate"
                fullWidth
                size="small"
                name="districtMagistrate"
                value={formData.districtMagistrate}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid>

            {/* Population */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Population"
                fullWidth
                size="small"
                type="number"
                name="population"
                value={formData.population}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid>

            {/* Registrar Office */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Registrar Office"
                fullWidth
                size="small"
                name="registrarOffice"
                value={formData.registrarOffice}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
              />
            </Grid>

            {/* Area with Unit */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="flex-end" gap={1}>
                <TextField
                  label="Area"
                  fullWidth
                  size="small"
                  type="number"
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                />
                <TextField
                  select
                  size="small"
                  name="areaUnit"
                  value={formData.areaUnit}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="sqft">Sq.ft</MenuItem>
                  <MenuItem value="sqm">Sq.m</MenuItem>
                  <MenuItem value="acre">Acre</MenuItem>
                  <MenuItem value="hectare">Hectare</MenuItem>
                  <MenuItem value="Meter">Meter</MenuItem>
                  <MenuItem value="km">KM</MenuItem>
                  <MenuItem value="sqkm">Sq.Km</MenuItem>
                </TextField>
              </Box>
            </Grid>

            {/* Circle Rate with Unit */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="flex-end" gap={1}>
                <TextField
                  label="Circle Rate (INR)"
                  fullWidth
                  size="small"
                  name="circleRate"
                  type="number"
                  value={formData.circleRate}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                />
                <TextField
                  select
                  size="small"
                  name="circleRateUnit"
                  value={formData.circleRateUnit}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                  sx={{ minWidth: 100 }}
                >
                    <MenuItem value="INR/sqft">INR/sqft</MenuItem>
                    <MenuItem value="INR/sqm">INR/sqm</MenuItem>
                    <MenuItem value="INR/acre">INR/acre</MenuItem>
                </TextField>
              </Box>
            </Grid>

            {/* Dates */}
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  name="startDate"
                  value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ""}
                  onChange={(e) => handleDateChange(new Date(e.target.value), "startDate")}
                  variant="outlined"
                  margin="dense"
                />
                <TextField
                  label="Finish Date"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  name="endDate"
                  value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ""}
                  onChange={(e) => handleDateChange(new Date(e.target.value), "endDate")}
                  variant="outlined"
                  margin="dense"
                />
              </Box>
            </Grid>

            {/* Checkboxes Section - Compact Layout */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                Components:
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="transportation_access"
                        checked={formData.transportation_access}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                        sx={{ py: 0 }}
                      />
                    }
                    label="Transportation Access"
                    sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' }, m: 0 }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="nearest_infra"
                        checked={formData.nearest_infra}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                        sx={{ py: 0 }}
                      />
                    }
                    label="Nearest Infrastructure"
                    sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' }, m: 0 }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="social_impact"
                        checked={formData.social_impact}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                        sx={{ py: 0 }}
                      />
                    }
                    label="Social Impact"
                    sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' }, m: 0 }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="job_impact"
                        checked={formData.job_impact}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                        sx={{ py: 0 }}
                      />
                    }
                    label="Job Impact"
                    sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' }, m: 0 }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="population_projection"
                        checked={formData.population_projection}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                        sx={{ py: 0 }}
                      />
                    }
                    label="Population Projection"
                    sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' }, m: 0 }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="land_price"
                        checked={formData.land_price}
                        onChange={handleChange}
                        color="primary"
                        size="small"
                        sx={{ py: 0 }}
                      />
                    }
                    label="Land Price"
                    sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' }, m: 0 }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* KML & Document Upload - Side by Side */}
            <Grid container spacing={2} sx={{ mt: 1.5 }}>
              {/* KML File Upload */}
              <Grid item xs={12} md={6}>
                <Button
                  component="label" 
                  fullWidth
                  variant="outlined"
                  sx={{
                    py: 0.5,
                    border: showRequiredError && !formData.kmlFile && !formData.documentFile
                      ? '1px dashed red'
                      : '1px dashed #3b82f6',
                    color: showRequiredError && !formData.kmlFile && !formData.documentFile
                      ? 'red'
                      : '#3b82f6',
                    fontWeight: 'bold',
                    borderRadius: 1,
                    position: 'relative',
                    transition: 'border-color 0.5s ease-out, color 0.5s ease-out',
                    fontSize: '0.8rem',
                    mb: 0.5
                  }}
                >
                  <MapPinned size={16} style={{ marginRight: '4px' }} />
                  Upload KML
                  <input
                    type="file"
                    name="kmlFile"
                    accept=".kml, .kmz"
                    multiple
                    onChange={handleChange}
                    disabled={isUploading}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </Button>
                {formData.kmlFile && (
                  <Typography
                    sx={{
                      color: '#3b82f6',
                      fontWeight: 'medium',
                      textAlign: 'center',
                      mt: 0,
                      fontSize: '0.75rem'
                    }}
                  >
                    {formData.kmlFile.name}
                  </Typography>
                )}
              </Grid>

              {/* Document Upload */}
              <Grid item xs={12} md={6}>
                <Button
                  component="label"
                  fullWidth
                  variant="outlined"
                  sx={{
                    py: 0.5,
                    border: showRequiredError && !formData.kmlFile && !formData.documentFile
                      ? '1px dashed red'
                      : '1px dashed #3b82f6',
                    color: showRequiredError && !formData.kmlFile && !formData.documentFile
                      ? 'red'
                      : '#3b82f6',
                    fontWeight: 'bold',
                    borderRadius: 1,
                    position: 'relative',
                    transition: 'border-color 0.5s ease-out, color 0.5s ease-out',
                    fontSize: '0.8rem',
                    mb: 0.5
                  }}
                >
                  <FilePlus size={16} style={{ marginRight: '4px' }} />
                  Upload Document
                  <input
                    type="file"
                    name="documentFile"
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx"
                  />
                </Button>
                {fileName && (
                  <Typography
                    sx={{
                      color: '#3b82f6',
                      fontWeight: 'medium',
                      textAlign: 'center',
                      mt: 0,
                      fontSize: '0.75rem'
                    }}
                  >
                    {fileName}
                  </Typography>
                )}
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={{ 
                  py: 0.75, 
                  background: 'linear-gradient(135deg, #3b82f6, #6d28d9)', 
                  color: '#fff', 
                  fontWeight: 'bold', 
                  borderRadius: 1,
                  '&:disabled': {
                    background: '#e0e0e0',
                    color: '#9e9e9e'
                  },
                  mt: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Add Sector Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-lg p-4 w-72 relative" onClick={(e) => e.stopPropagation()}>
              <button
                className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
                onClick={() => setShowModal(false)}
              >
                <X size={14} />
              </button>
              <div className="flex flex-col items-center">
                <h2 className="text-lg font-bold mb-2">Add New Sector</h2>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleAddSector();
                }} className="w-full">
                  <TextField
                    autoComplete="off"
                    label="New Sector"
                    fullWidth
                    size="small"
                    value={newSector}
                    onChange={(e) => setNewSector(e.target.value)}
                    variant="outlined"
                    margin="dense"
                    className={shouldShake("sector")}
                    error={!!sectorError}
                    helperText={sectorError}
                    sx={{ mb: 1 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSector();
                      }
                    }}
                  />
                  <div className="flex justify-center w-full">
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        py: 0.5,
                        background: 'linear-gradient(135deg, #3b82f6, #6d28d9)',
                        color: '#fff',
                        fontWeight: 'bold',
                        borderRadius: 1,
                        width: '100%',
                        maxWidth: '100px',
                        fontSize: '0.875rem'
                      }}
                      onClick={handleAddSector}
                    >
                      Add
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-md p-4 w-72 relative">
              <button onClick={closeSuccessModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
                <X className="w-4 h-4" />
              </button>
              <div className="flex flex-col items-center">
                <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                <h2 className="text-lg font-bold mb-1">Success!</h2>
                <p className="text-gray-600 text-center text-xs">
                  Project submitted successfully.
                </p>
              </div>
            </div>
          </div>
        )}
 

        {/* Update Modal */}
        {showUpdateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 w-72 relative">
              <button onClick={closeUpdateModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
                <X className="w-4 h-4" />
              </button>
              <div className="flex flex-col items-center">
                <Edit className="w-8 h-8 text-blue-500 mb-2" />
                <h2 className="text-lg font-bold mb-1">Updated!</h2>
                <p className="text-gray-600 text-center text-xs">Project updated successfully.</p>
              </div>
            </div>
          </div>
        )}
      </Paper>
    </Box>
  );
};

export default AddProject1;


// import React, { useState, useEffect, useRef } from "react";
// import axios from 'axios';
// import "react-datepicker/dist/react-datepicker.css";
// import { Country, State, City } from "country-state-city";
// import { Plus, Upload, X, CheckCircle, Trash, Edit, FilePlus, MapPinned, Loader2 } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { TextField, MenuItem, Button, Box, Typography, Grid, Paper, Divider, IconButton, FormControl, InputLabel, Select } from '@mui/material';

// import axiosInstance from "../axiosInstance";
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const AddProject1 = ({ editingProject, onClose , handleReloadPrjects}) => {
//   const [formData, setFormData] = useState({
//     project_name: "",
//     sector: "",
//     cost: "",
//     status: "",
//     country: "",
//     state: "",
//     city: "",
//     startDate: null,
//     endDate: null,
//     contractor: "",
//     districtMagistrate: '',
//     population: '',
//     registrarOffice: '',
//     area: '',
//     areaUnit: 'sqft',
//     circleRate: '',
//     circleRateUnit: 'sqft',
//     kmlFile: null,
//     documentFile: null,
//   });

//   const [sectors, setSectors] = useState(["Commercial", "Construction", "Education", "Factory", "Highways", "Infrastructure", "Mall", "Railways", "Residential", "Retail"]);
//   const [newSector, setNewSector] = useState("");
//   const [showModal, setShowModal] = useState(false);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [shakeFields, setShakeFields] = useState([]);
//   const [sectorError, setSectorError] = useState("");
//   const [fileName, setFileName] = useState('');
//   const [kmlFileName, setKmlFileName] = useState('');
//   const [message, setMessage] = useState("");
//   const [isUploading, setIsUploading] = useState(false);
//   const [kmlFiles, setKmlFiles] = useState([]);
//   const [showRequiredError, setShowRequiredError] = useState(false);
//   const [errorFields, setErrorFields] = useState([]);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const companyId = '67ce66294f28f85e4ff91e2c';
//   const navigate = useNavigate();
//   const formRef = useRef(null);

//   useEffect(() => {
//     if (editingProject) {
//       setFormData({
//         ...editingProject,
//         startDate: editingProject.startDate ? new Date(editingProject.startDate) : null,
//         endDate: editingProject.endDate ? new Date(editingProject.endDate) : null,
//         areaUnit: editingProject.areaUnit || 'sqft',
//         circleRateUnit: editingProject.circleRateUnit || 'sqft'
//       });
//       if (editingProject.kmlFile) {
//         setKmlFileName(editingProject.kmlFile.name || 'KML file uploaded');
//       }
//     }
//   }, [editingProject]);

//   useEffect(() => {
//     if (errorFields.length > 0) {
//       const timer = setTimeout(() => {
//         setErrorFields([]);
//       }, 2000);
//       return () => clearTimeout(timer);
//     }
//   }, [errorFields]);

//   const handleAddSector = () => {
//     if (newSector.trim() && !sectors.includes(newSector.trim())) {
//       setSectors((prev) => [...prev, newSector.trim()]);
//       setNewSector("");
//       setShowModal(false);
//       setSectorError("");
//     } else {
//       setSectorError("Sector already exists or invalid input!");
//       setShakeFields(["sector"]);
//     }
//   };

//   const handleDeleteSector = (sectorToDelete) => {
//     setSectors((prev) => prev.filter((sector) => sector !== sectorToDelete));
//     if (formData.sector === sectorToDelete) {
//       setFormData({ ...formData, sector: "" });
//     }
//   };

//   const getCountryCode = (countryName) => {
//     const country = Country.getAllCountries().find((c) => c.name === countryName);
//     return country ? country.isoCode : "";
//   };

//   const getStateCode = (stateName, countryCode) => {
//     const state = State.getStatesOfCountry(countryCode).find((s) => s.name === stateName);
//     return state ? state.isoCode : "";
//   };

//   const [document, setDocument] = useState("");

//   const handleChange = (e) => {
//     const { name, value, files } = e.target;
//     if (name === "kmlFile") {
//       const selectedFiles = Array.from(files);
//       setKmlFiles(selectedFiles);
//       setFormData({
//         ...formData,
//         [name]: selectedFiles[0],
//       });
//       if (selectedFiles.length > 0) {
//         setKmlFileName(
//           selectedFiles.length > 1
//             ? `${selectedFiles.length} files selected`
//             : selectedFiles[0].name
//         );
//       } else {
//         setKmlFileName('');
//       }
//     } else if (name === "documentFile") {
//       const file = files[0];
//       setDocument(file)
//       setFormData({
//         ...formData,
//         [name]: file,
//       });
//       setFileName(file ? file.name : '');
//     } else if (name === "country") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         state: "",
//         city: "",
//       });
//     } else if (name === "state") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         city: "",
//       });
//     } else {
//       setFormData({
//         ...formData,
//         [name]: value,
//       });
//     }
//   };

//   const handleDateChange = (date, field) => {
//     setFormData({
//       ...formData,
//       [field]: date,
//     });
//   };

//   const validateForm = () => {
//     const requiredFields = [
//       'project_name',
//       'sector',
//       'cost',
//       'status',
//       'country',
//       'state',
//       'city'
//     ];
    
//     const emptyFields = requiredFields.filter(field => !formData[field]);
    
//     if (emptyFields.length > 0 || (!formData.kmlFile && !formData.documentFile)) {
//       setShakeFields(emptyFields);
//       setErrorFields(emptyFields);
//       setShowRequiredError(true);
      
//       setTimeout(() => {
//         setShowRequiredError(false);
//         setErrorFields([]);
//       }, 2000);
      
//       return false;
//     }
    
//     setShowRequiredError(false);
//     return true;
//   };

//   const handleSubmit = async (payloadurl) => {
//     if (!validateForm()) {
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       const payload = {
//         project_name: formData.project_name,
//         sector: formData.sector,
//         cost: formData.cost,
//         status: formData.status,
//         country: formData.country,
//         state: formData.state,
//         city: formData.city,
//         startDate: formData.startDate?.toISOString().split("T")[0],
//         endDate: formData.endDate?.toISOString().split("T")[0],
//         contractor: formData.contractor,
//         districtMagistrate: formData.districtMagistrate,
//         population: formData.population,
//         registrarOffice: formData.registrarOffice,
//         area: formData.area,
//         areaUnit: formData.areaUnit,
//         circleRate: formData.circleRate,
//         circleRateUnit: formData.circleRateUnit,
//         kml: payloadurl.kmlUrls ? payloadurl.kmlUrls : [],
//         documentFile: payloadurl.documentUrl ? payloadurl.documentUrl : [],
//       };

//       if (editingProject) {
//         const response = await axiosInstance.put(
//           `/project/updateProject`,
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'Content-Type': 'application/json',
//               'x-project-id': editingProject._id
//             },
//           }
//         );
//         // console.log('Project updated successfully:', response.data);
//         setShowUpdateModal(true);
//       } else {
//         const response = await axiosInstance.post(
//           '/project/createProject',
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'x-auth-token': localStorage.getItem('token'),
//               'Content-Type': 'application/json',
//             },
//           }
//         );
//         // console.log('Project created successfully:', response.data);
//         setShowSuccessModal(true);
//       }
//     } catch (error) {
//       console.error('Error submitting project:', error.response?.data || error.message);
//       toast.error('Error submitting project');
//     } finally {
//       handleReloadPrjects()
//       setIsSubmitting(false);
//     }
//   };

//   const closeSuccessModal = () => {
//     setShowSuccessModal(false);
//     onClose();
//   };

//   const closeUpdateModal = () => {
//     setShowUpdateModal(false);
//     onClose();
//   };

//   const shouldShake = (fieldName) => {
//     return shakeFields.includes(fieldName) ? "animate-shake" : "";
//   };

//   const handleKmlUpload = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
    
//     if (!validateForm()) {
//       setIsSubmitting(false);
//       return;
//     }
    
//     if (kmlFiles.length === 0 && !document) {
//       setMessage("No files selected!");
//       await handleSubmit({});
//       return;
//     }

//     setIsUploading(true);
//     setMessage("Uploading files...");

//     try {
//       let uploadedKmlUrls = [];
//       let documentUrl = null;

//       if (kmlFiles.length > 0) {
//         for (const file of kmlFiles) {
//           const formData = new FormData();
//           formData.append("file", file);
//           formData.append("upload_preset", "ml_default");

//           const response = await axios.post(
//             "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//             formData
//           );
//           uploadedKmlUrls.push(response.data.secure_url);
//         }
//       }

//       if (document) {
//         const formData = new FormData();
//         formData.append("file", document);
//         formData.append("upload_preset", "ml_default");

//         const response = await axios.post(
//           "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//           formData
//         );
//         documentUrl = response.data.secure_url;
//       }

//       const payload = {
//         kmlUrls: uploadedKmlUrls.length > 0 ? uploadedKmlUrls : null,
//         documentUrl: documentUrl || null,
//       };

//       await handleSubmit(payload);
//     } catch (error) {
//       toast.error("Upload failed");
//       console.error("Upload Error:", error);
//     } finally {
//       setIsUploading(false);
//       setKmlFiles([]);
//     }
//   };

//   const renderLabel = (fieldName, label) => {
//     const requiredFields = ['project_name', 'sector', 'cost', 'status', 'country', 'state', 'city'];
//     const isRequired = requiredFields.includes(fieldName);
//     const isMissing = errorFields.includes(fieldName);
    
//     return (
//       <span>
//         {label}
//         {isRequired && <span style={{ 
//           color: isMissing ? 'red' : 'inherit',
//           transition: 'color 0.5s ease-out'
//         }}>*</span>}
//       </span>
//     );
//   };

//   const handlePlusButtonClick = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setShowModal(true);
//   };

//   return (
//     <Box display="flex" justifyContent="center" alignItems="flex-start" p={2} ref={formRef}>
//       <ToastContainer position="top-right" autoClose={5000} />
//       <Paper elevation={3} sx={{ p: 2, borderRadius: 2, maxWidth: 700, width: '100%', background: '#fff', mt: 0, position: 'relative' }}>
//         <button
//           className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//           onClick={onClose}
//         >
//           <X size={14} />
//         </button>

//         <Typography variant="h5" align="center" fontWeight="bold" color="primary" gutterBottom>
//           {editingProject ? "Edit Project" : "Add Project"}
//         </Typography>
//         <Divider sx={{ my: 1 }} />
//         <form onSubmit={(e) => handleKmlUpload(e)} autoComplete="off">
//           <Grid container spacing={1.5}>
//             {/* Project Name */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("project_name", "Project Name")}
//                 fullWidth
//                 size="small"
//                 name="project_name"
//                 value={formData.project_name}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("project_name")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("project_name") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Sector */}
//             <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
//               <TextField
//                 label={renderLabel("sector", "Sector")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="sector"
//                 value={formData.sector}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("sector")}
//                 SelectProps={{
//                   renderValue: (selected) => selected,
//                 }}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("sector") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Sector</MenuItem>
//                 {sectors.map((sector, index) => (
//                   <MenuItem key={index} value={sector}>
//                     <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
//                       {sector}
//                       {formData.sector !== sector && (
//                         <IconButton
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleDeleteSector(sector);
//                           }}
//                           size="small"
//                         >
//                           <X size={14} />
//                         </IconButton>
//                       )}
//                     </Box>
//                   </MenuItem>
//                 ))}
//               </TextField>
            
//               <button
//                 className="absolute top-1 -right-3 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={handlePlusButtonClick}
//               >
//                 <Plus size={18} />
//               </button>
//             </Grid>

//             {/* Cost */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("cost", "Cost (Cr)")}
//                 type="number"
//                 fullWidth
//                 size="small"
//                 name="cost"
//                 value={formData.cost}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("cost")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("cost") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Status */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("status", "Status")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="status"
//                 value={formData.status}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("status")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("status") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Status</MenuItem>
//                 <MenuItem value="Conceptual">Conceptual</MenuItem>
//                 <MenuItem value="DPR">DPR</MenuItem>
//                 <MenuItem value="Tender">Tender</MenuItem>
//                 <MenuItem value="Under-Construction">Under-Construction</MenuItem>
//                 <MenuItem value="Constructed">Constructed</MenuItem>
//               </TextField>
//             </Grid>

//             {/* Country */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("country", "Country")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="country"
//                 value={formData.country}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("country")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("country") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Country</MenuItem>
//                 {Country.getAllCountries().map((country) => (
//                   <MenuItem key={country.isoCode} value={country.name}>
//                     {country.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* State */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("state", "State")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="state"
//                 value={formData.state}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("state")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("state") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select State</MenuItem>
//                 {State.getStatesOfCountry(getCountryCode(formData.country)).map((state) => (
//                   <MenuItem key={state.isoCode} value={state.name}>
//                     {state.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* City */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("city", "City")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="city"
//                 value={formData.city}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("city")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("city") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select City</MenuItem>
//                 {City.getCitiesOfState(getCountryCode(formData.country), getStateCode(formData.state, getCountryCode(formData.country))).map((city) => (
//                   <MenuItem key={city.name} value={city.name}>
//                     {city.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* Contractor */}
//             <Grid item xs={12} md={6}>
//               <TextField label="Contractor" fullWidth size="small" name="contractor" value={formData.contractor} onChange={handleChange} variant="outlined" />
//             </Grid>

//             {/* District Magistrate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="District Magistrate"
//                 fullWidth
//                 size="small"
//                 name="districtMagistrate"
//                 value={formData.districtMagistrate}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* Population */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Population"
//                 fullWidth
//                 size="small"
//                 type="number"
//                 name="population"
//                 value={formData.population}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* Registrar Office */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Registrar Office"
//                 fullWidth
//                 size="small"
//                 name="registrarOffice"
//                 value={formData.registrarOffice}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* Area with Unit */}
//             <Grid item xs={12} md={6}>
//               <Box display="flex" alignItems="flex-end" gap={1}>
//                 <TextField
//                   label="Area"
//                   fullWidth
//                   size="small"
//                   type="number"
//                   name="area"
//                   value={formData.area}
//                   onChange={handleChange}
//                   variant="outlined"
//                 />
//                 <TextField
//                   select
//                   size="small"
//                   name="areaUnit"
//                   value={formData.areaUnit}
//                   onChange={handleChange}
//                   variant="outlined"
//                   sx={{ minWidth: 120 }}
//                 >
//                   <MenuItem value="sqft">Sq.ft</MenuItem>
//                   <MenuItem value="sqm">Sq.m</MenuItem>
//                   <MenuItem value="acre">Acre</MenuItem>
//                   <MenuItem value="hectare">Hectare</MenuItem>
//                   <MenuItem value="Meter">Meter</MenuItem>
//                   <MenuItem value="km">KM</MenuItem>
//                   <MenuItem value="sqkm">Sq.Km</MenuItem>
//                 </TextField>
//               </Box>
//             </Grid>

//           {/* Circle Rate with Unit */}
//           <Grid item xs={12} md={6}>
//               <Box display="flex" alignItems="flex-end" gap={1}>
//                 <TextField
//                   label="Circle Rate (INR)"
//                   fullWidth
//                   size="small"
//                   name="circleRate"
//                   type="number"
//                   value={formData.circleRate}
//                   onChange={handleChange}
//                   variant="outlined"
//                 />
//                 <TextField
//                   select
//                   size="small"
//                   name="circleRateUnit"
//                   value={formData.circleRateUnit}
//                   onChange={handleChange}
//                   variant="outlined"
//                   sx={{ minWidth: 120 }}
//                 >
//                     <MenuItem value="INR/sqft">INR/sqft</MenuItem>
//                     <MenuItem value="INR/sqm">INR/sqm</MenuItem>
//                     <MenuItem value="INR/acre">INR/acre</MenuItem>
//                 </TextField>
//               </Box>
//             </Grid>



//             {/* Start Date */}
//             <Grid container  xs={8} md={4}  >
//             <Box display="flex" alignItems="flex-end" gap={1}>
                  
//                   <TextField
//                     label="Start Date"
//                     type="date"
//                     fullWidth
//                     size="small"
//                     InputLabelProps={{ shrink: true }}
//                     name="startDate"
//                     value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ""}
//                     onChange={(e) => handleDateChange(new Date(e.target.value), "startDate")}
//                     variant="outlined"
//                     sx={{ minWidth: 120 }}
//                   />
                 

                
//                   <TextField
//                     label="Finish Date"
//                     type="date"
//                     fullWidth
//                     size="small"
//                     InputLabelProps={{ shrink: true }}
//                     name="endDate"
//                     value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ""}
//                     onChange={(e) => handleDateChange(new Date(e.target.value), "endDate")}
//                     variant="outlined"
//                     sx={{ minWidth: 120 }}
//                   />
                
//                 </Box>
//               </Grid>


//             {/* KML File Upload */}
//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{ 
//                   py: 1, 
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6', 
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2, 
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out',
//                   marginBottom: 1
//                 }}
//               >
//                 <MapPinned size={18} style={{ marginRight: '6px' }} />
//                 Upload KML
//                 <input type="file" name="kmlFile" accept=".kml, .kmz" multiple onChange={handleChange} disabled={isUploading} className="absolute inset-0 opacity-0 cursor-pointer" />
//               </Button>
//               {formData.kmlFile && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 0 }}>
//                   {formData.kmlFile.name}
//                 </Typography>
//               )}
//             </Grid>

//             {/* Document Upload */}
//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{
//                   py: 1,
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6',
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6',
//                   fontWeight: 'bold',
//                   borderRadius: 2,
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out',
//                   marginBottom: 1
//                 }}
//               >
//                 <FilePlus size={18} style={{ marginRight: '6px' }} />
//                 Upload Document
//                 <input
//                   type="file"
//                   name="documentFile"
//                   onChange={handleChange}
//                   style={{ display: 'none' }}
//                   accept=".pdf,.doc,.docx"
//                 />
//               </Button>
//               {fileName && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 0 }}>
//                   {fileName}
//                 </Typography>
//               )}
//             </Grid>

//             {/* Submit Button */}
//             <Grid item xs={12}>
//               <Button
//                 type="submit"
//                 fullWidth
//                 variant="contained"
//                 disabled={isSubmitting}
//                 sx={{ 
//                   py: 1, 
//                   background: 'linear-gradient(135deg, #3b82f6, #6d28d9)', 
//                   color: '#fff', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2,
//                   '&:disabled': {
//                     background: '#e0e0e0',
//                     color: '#9e9e9e'
//                   },
//                   marginTop: 1
//                 }}
//               >
//                 {isSubmitting ? (
//                   <>
//                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
//                     Submitting...
//                   </>
//                 ) : (
//                   'Submit'
//                 )}
//               </Button>
//             </Grid>
//           </Grid>
//         </form>

//         {/* Add Sector Modal */}
//         {showModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
//             <div className="bg-white rounded-lg p-6 w-80 relative" onClick={(e) => e.stopPropagation()}>
//               <button
//                 className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(false)}
//               >
//                 <X size={14} />
//               </button>
//               <div className="flex flex-col items-center">
//                 <h2 className="text-xl font-bold mb-3">Add New Sector</h2>
//                 <form onSubmit={(e) => {
//                   e.preventDefault();
//                   handleAddSector();
//                 }} className="w-full">
//                   <TextField
//                     autoComplete="off"
//                     label="New Sector"
//                     fullWidth
//                     size="small"
//                     value={newSector}
//                     onChange={(e) => setNewSector(e.target.value)}
//                     variant="outlined"
//                     className={shouldShake("sector")}
//                     error={!!sectorError}
//                     helperText={sectorError}
//                     sx={{ mb: 2 }}
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         e.preventDefault();
//                         handleAddSector();
//                       }
//                     }}
//                   />
//                   <div className="flex justify-center w-full">
//                     <Button
//                       type="submit"
//                       variant="contained"
//                       sx={{
//                         py: 1,
//                         background: 'linear-gradient(135deg, #3b82f6, #6d28d9)',
//                         color: '#fff',
//                         fontWeight: 'bold',
//                         borderRadius: 2,
//                         width: '100%',
//                         maxWidth: '120px'
//                       }}
//                       onClick={handleAddSector}
//                     >
//                       Add
//                     </Button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Success Modal */}
//         {showSuccessModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-xl shadow-md p-6 w-80 relative">
//               <button onClick={closeSuccessModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Congratulations!</h2>
//                 <p className="text-gray-600 text-center text-sm">
//                   Your project has been successfully submitted.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Update Modal */}
//         {showUpdateModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 w-80 relative">
//               <button onClick={closeUpdateModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <Edit className="w-10 h-10 text-blue-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Successfully Updated!</h2>
//                 <p className="text-gray-600 text-center text-sm">Your project has been successfully updated.</p>
//               </div>
//             </div>
//           </div>
//         )}
//       </Paper>
//     </Box>
//   );
// };

// export default AddProject1;
// new code ..............
// import React, { useState, useEffect, useRef } from "react";
// import axios from 'axios';
// import "react-datepicker/dist/react-datepicker.css";
// import { Country, State, City } from "country-state-city";
// import { Plus, Upload, X, CheckCircle, Trash, Edit, FilePlus, MapPinned, Loader2 } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { TextField, MenuItem, Button, Box, Typography, Grid, Paper, Divider, IconButton } from '@mui/material';
// import axiosInstance from "../axiosInstance";
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const AddProject1 = ({ editingProject, onClose , handleReloadPrjects}) => {
//   const [formData, setFormData] = useState({
//     project_name: "",
//     sector: "",
//     cost: "",
//     status: "",
//     country: "",
//     state: "",
//     city: "",
//     startDate: null,
//     endDate: null,
//     contractor: "",
//     districtMagistrate: '',
//     population: '',
//     registrarOffice: '',
//     circleRate: '',
//     kmlFile: null,
//     documentFile: null,
//   });

//   const [sectors, setSectors] = useState(["Commercial", "Construction", "Education", "Factory", "Highways", "Infrastructure", "Mall", "Railways", "Residential", "Retail"]);
//   const [newSector, setNewSector] = useState("");
//   const [showModal, setShowModal] = useState(false);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [shakeFields, setShakeFields] = useState([]);
//   const [sectorError, setSectorError] = useState("");
//   const [fileName, setFileName] = useState('');
//   const [kmlFileName, setKmlFileName] = useState('');
//   const [message, setMessage] = useState("");
//   const [isUploading, setIsUploading] = useState(false);
//   const [kmlFiles, setKmlFiles] = useState([]);
//   const [showRequiredError, setShowRequiredError] = useState(false);
//   const [errorFields, setErrorFields] = useState([]);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const companyId = '67ce66294f28f85e4ff91e2c';
//   const navigate = useNavigate();
//   const formRef = useRef(null);

//   useEffect(() => {
//     if (editingProject) {
//       setFormData({
//         ...editingProject,
//         startDate: editingProject.startDate ? new Date(editingProject.startDate) : null,
//         endDate: editingProject.endDate ? new Date(editingProject.endDate) : null,
//       });
//       if (editingProject.kmlFile) {
//         setKmlFileName(editingProject.kmlFile.name || 'KML file uploaded');
//       }
//     }
//   }, [editingProject]);

//   useEffect(() => {
//     if (errorFields.length > 0) {
//       const timer = setTimeout(() => {
//         setErrorFields([]);
//       }, 2000);
//       return () => clearTimeout(timer);
//     }
//   }, [errorFields]);

//   const handleAddSector = () => {
//     if (newSector.trim() && !sectors.includes(newSector.trim())) {
//       setSectors((prev) => [...prev, newSector.trim()]);
//       setNewSector("");
//       setShowModal(false);
//       setSectorError("");
//     } else {
//       setSectorError("Sector already exists or invalid input!");
//       setShakeFields(["sector"]);
//     }
//   };

//   const handleDeleteSector = (sectorToDelete) => {
//     setSectors((prev) => prev.filter((sector) => sector !== sectorToDelete));
//     if (formData.sector === sectorToDelete) {
//       setFormData({ ...formData, sector: "" });
//     }
//   };

//   const getCountryCode = (countryName) => {
//     const country = Country.getAllCountries().find((c) => c.name === countryName);
//     return country ? country.isoCode : "";
//   };

//   const getStateCode = (stateName, countryCode) => {
//     const state = State.getStatesOfCountry(countryCode).find((s) => s.name === stateName);
//     return state ? state.isoCode : "";
//   };

//   const [document, setDocument] = useState("");

//   const handleChange = (e) => {
//     const { name, value, files } = e.target;
//     if (name === "kmlFile") {
//       const selectedFiles = Array.from(files);
//       setKmlFiles(selectedFiles);
//       setFormData({
//         ...formData,
//         [name]: selectedFiles[0],
//       });
//       if (selectedFiles.length > 0) {
//         setKmlFileName(
//           selectedFiles.length > 1
//             ? `${selectedFiles.length} files selected`
//             : selectedFiles[0].name
//         );
//       } else {
//         setKmlFileName('');
//       }
//     } else if (name === "documentFile") {
//       const file = files[0];
//       setDocument(file)
//       setFormData({
//         ...formData,
//         [name]: file,
//       });
//       setFileName(file ? file.name : '');
//     } else if (name === "country") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         state: "",
//         city: "",
//       });
//     } else if (name === "state") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         city: "",
//       });
//     } else {
//       setFormData({
//         ...formData,
//         [name]: value,
//       });
//     }
//   };

//   const handleDateChange = (date, field) => {
//     setFormData({
//       ...formData,
//       [field]: date,
//     });
//   };

//   const validateForm = () => {
//     const requiredFields = [
//       'project_name',
//       'sector',
//       'cost',
//       'status',
//       'country',
//       'state',
//       'city'
//     ];
    
//     const emptyFields = requiredFields.filter(field => !formData[field]);
    
//     if (emptyFields.length > 0 || (!formData.kmlFile && !formData.documentFile)) {
//       setShakeFields(emptyFields);
//       setErrorFields(emptyFields);
//       setShowRequiredError(true);
      
//       setTimeout(() => {
//         setShowRequiredError(false);
//         setErrorFields([]);
//       }, 2000);
      
//       return false;
//     }
    
//     setShowRequiredError(false);
//     return true;
//   };

//   const handleSubmit = async (payloadurl) => {
//     if (!validateForm()) {
//       setIsSubmitting(false);
//       return;
//     }
    
//     try {
//       const payload = {
//         project_name: formData.project_name,
//         sector: formData.sector,
//         cost: formData.cost,
//         status: formData.status,
//         country: formData.country,
//         state: formData.state,
//         city: formData.city,
//         startDate: formData.startDate?.toISOString().split("T")[0],
//         endDate: formData.endDate?.toISOString().split("T")[0],
//         contractor: formData.contractor,
//         districtMagistrate: formData.districtMagistrate,
//         population: formData.population,
//         registrarOffice: formData.registrarOffice,
//         circleRate: formData.circleRate,
//         kml: payloadurl.kmlUrls ? payloadurl.kmlUrls : [],
//         documentFile: payloadurl.documentUrl ? payloadurl.documentUrl : [],
//       };

//       if (editingProject) {
//         const response = await axiosInstance.put(
//           `/project/updateProject`,
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'Content-Type': 'application/json',
//               'x-project-id': editingProject._id
//             },
//           }
//         );
//         console.log('Project updated successfully:', response.data);
//         setShowUpdateModal(true);
//       } else {
//         const response = await axiosInstance.post(
//           '/project/createProject',
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'x-auth-token': localStorage.getItem('token'),
//               'Content-Type': 'application/json',
//             },
//           }
//         );
//         console.log('Project created successfully:', response.data);
//         setShowSuccessModal(true);
//       }
//     } catch (error) {
//       console.error('Error submitting project:', error.response?.data || error.message);
//       toast.error('Error submitting project');
//     } finally {
//       handleReloadPrjects()
//       setIsSubmitting(false);
//     }
//   };

//   const closeSuccessModal = () => {
//     setShowSuccessModal(false);
//     onClose();
//   };

//   const closeUpdateModal = () => {
//     setShowUpdateModal(false);
//     onClose();
//   };

//   const shouldShake = (fieldName) => {
//     return shakeFields.includes(fieldName) ? "animate-shake" : "";
//   };

//   const handleKmlUpload = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);
    
//     if (!validateForm()) {
//       setIsSubmitting(false);
//       return;
//     }
    
//     if (kmlFiles.length === 0 && !document) {
//       setMessage("No files selected!");
//       await handleSubmit({});
//       return;
//     }

//     setIsUploading(true);
//     setMessage("Uploading files...");

//     try {
//       let uploadedKmlUrls = [];
//       let documentUrl = null;

//       if (kmlFiles.length > 0) {
//         for (const file of kmlFiles) {
//           const formData = new FormData();
//           formData.append("file", file);
//           formData.append("upload_preset", "ml_default");

//           const response = await axios.post(
//             "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//             formData
//           );
//           uploadedKmlUrls.push(response.data.secure_url);
//         }
//       }

//       if (document) {
//         const formData = new FormData();
//         formData.append("file", document);
//         formData.append("upload_preset", "ml_default");

//         const response = await axios.post(
//           "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//           formData
//         );
//         documentUrl = response.data.secure_url;
//       }

//       const payload = {
//         kmlUrls: uploadedKmlUrls.length > 0 ? uploadedKmlUrls : null,
//         documentUrl: documentUrl || null,
//       };

//       await handleSubmit(payload);
//     } catch (error) {
//       toast.error("Upload failed");
//       console.error("Upload Error:", error);
//     } finally {
//       setIsUploading(false);
//       setKmlFiles([]);
//     }
//   };

//   const renderLabel = (fieldName, label) => {
//     const requiredFields = ['project_name', 'sector', 'cost', 'status', 'country', 'state', 'city'];
//     const isRequired = requiredFields.includes(fieldName);
//     const isMissing = errorFields.includes(fieldName);
    
//     return (
//       <span>
//         {label}
//         {isRequired && <span style={{ 
//           color: isMissing ? 'red' : 'inherit',
//           transition: 'color 0.5s ease-out'
//         }}>*</span>}
//       </span>
//     );
//   };

//   // New function to handle plus button click
//   const handlePlusButtonClick = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     setShowModal(true);
//   };

//   return (
//     <Box display="flex" justifyContent="center" alignItems="flex-start" p={2} ref={formRef}>
//       <ToastContainer position="top-right" autoClose={5000} />
//       <Paper elevation={3} sx={{ p: 3, borderRadius: 4, maxWidth: 750, width: '100%', background: '#fff', mt: 0, position: 'relative' }}>
//         <button
//           className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//           onClick={onClose}
//         >
//           <X size={14} />
//         </button>

//         <Typography variant="h5" align="center" fontWeight="bold" color="primary" gutterBottom>
//           {editingProject ? "Edit Project" : "Add Project"}
//         </Typography>
//         <Divider sx={{ my: 2 }} />
//         <form onSubmit={(e) => handleKmlUpload(e)} autoComplete="off">
//           <Grid container spacing={2}>
//             {/* Project Name */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("project_name", "Project Name")}
//                 fullWidth
//                 size="small"
//                 name="project_name"
//                 value={formData.project_name}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("project_name")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("project_name") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Sector */}
//             <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
//               <TextField
//                 label={renderLabel("sector", "Sector")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="sector"
//                 value={formData.sector}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("sector")}
//                 SelectProps={{
//                   renderValue: (selected) => selected,
//                 }}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("sector") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Sector</MenuItem>
//                 {sectors.map((sector, index) => (
//                   <MenuItem key={index} value={sector}>
//                     <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
//                       {sector}
//                       {formData.sector !== sector && (
//                         <IconButton
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleDeleteSector(sector);
//                           }}
//                           size="small"
//                         >
//                           <X size={14} />
//                         </IconButton>
//                       )}
//                     </Box>
//                   </MenuItem>
//                 ))}
//               </TextField>
            
//               <button
//                 className="absolute top-1 -right-3 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={handlePlusButtonClick}  // Updated to use the new handler
//               >
//                 <Plus size={18} />
//               </button>
//             </Grid>

//             {/* Cost */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("cost", "Cost (INR)")}
//                 type="number"
//                 fullWidth
//                 size="small"
//                 name="cost"
//                 value={formData.cost}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("cost")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("cost") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Status */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("status", "Status")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="status"
//                 value={formData.status}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("status")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("status") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Status</MenuItem>
//                 <MenuItem value="Conceptual">Conceptual</MenuItem>
//                 <MenuItem value="DPR">DPR</MenuItem>
//                 <MenuItem value="Tender">Tender</MenuItem>
//                 <MenuItem value="Under-Construction">Under-Construction</MenuItem>
//                 <MenuItem value="Constructed">Constructed</MenuItem>
//               </TextField>
//             </Grid>

//             {/* Country */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("country", "Country")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="country"
//                 value={formData.country}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("country")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("country") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Country</MenuItem>
//                 {Country.getAllCountries().map((country) => (
//                   <MenuItem key={country.isoCode} value={country.name}>
//                     {country.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* State */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("state", "State")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="state"
//                 value={formData.state}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("state")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("state") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select State</MenuItem>
//                 {State.getStatesOfCountry(getCountryCode(formData.country)).map((state) => (
//                   <MenuItem key={state.isoCode} value={state.name}>
//                     {state.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* City */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("city", "City")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="city"
//                 value={formData.city}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("city")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("city") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select City</MenuItem>
//                 {City.getCitiesOfState(getCountryCode(formData.country), getStateCode(formData.state, getCountryCode(formData.country))).map((city) => (
//                   <MenuItem key={city.name} value={city.name}>
//                     {city.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* Contractor */}
//             <Grid item xs={12} md={6}>
//               <TextField label="Contractor" fullWidth size="small" name="contractor" value={formData.contractor} onChange={handleChange} variant="outlined" />
//             </Grid>

//             {/* districtMagistrate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="District Magistrate"
//                 fullWidth
//                 size="small"
//                 name="districtMagistrate"
//                 value={formData.districtMagistrate}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* population */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Population"
//                 fullWidth
//                 size="small"
//                 type="number"
//                 name="population"
//                 value={formData.population}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* registrarOffice */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Registrar Office"
//                 fullWidth
//                 size="small"
//                 name="registrarOffice"
//                 value={formData.registrarOffice}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* circleRate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Circle Rate (INR)"
//                 fullWidth
//                 size="small"
//                 name="circleRate"
//                 type="number"
//                 value={formData.circleRate}
//                 onChange={handleChange}
//                 variant="outlined"
                
//               />
//             </Grid>

//             {/* Start Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Start Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="startDate"
//                 value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "startDate")}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* Finish Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Finish Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="endDate"
//                 value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "endDate")}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* KML File Upload */}
//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{ 
//                   py: 1, 
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6', 
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2, 
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out'
//                 }}
//               >
//                 <MapPinned size={18} style={{ marginRight: '6px' }} />
//                 Upload KML
//                 <input type="file" name="kmlFile" accept=".kml, .kmz" multiple onChange={handleChange} disabled={isUploading} className="absolute inset-0 opacity-0 cursor-pointer" />
//               </Button>
//               {formData.kmlFile && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {formData.kmlFile.name}
//                 </Typography>
//               )}
//             </Grid>

//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{
//                   py: 1,
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6',
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6',
//                   fontWeight: 'bold',
//                   borderRadius: 2,
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out'
//                 }}
//               >
//                 <FilePlus size={18} style={{ marginRight: '6px' }} />
//                 Upload Document
//                 <input
//                   type="file"
//                   name="documentFile"
//                   onChange={handleChange}
//                   style={{ display: 'none' }}
//                   accept=".pdf,.doc,.docx"
//                 />
//               </Button>
//               {fileName && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {fileName}
//                 </Typography>
//               )}
//             </Grid>

//             {/* Submit Button */}
//             <Grid item xs={12}>
//               <Button
//                 type="submit"
//                 fullWidth
//                 variant="contained"
//                 disabled={isSubmitting}
//                 sx={{ 
//                   py: 1, 
//                   background: 'linear-gradient(135deg, #3b82f6, #6d28d9)', 
//                   color: '#fff', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2,
//                   '&:disabled': {
//                     background: '#e0e0e0',
//                     color: '#9e9e9e'
//                   }
//                 }}
//               >
//                 {isSubmitting ? (
//                   <>
//                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
//                     Submitting...
//                   </>
//                 ) : (
//                   'Submit'
//                 )}
//               </Button>
//             </Grid>
//           </Grid>
//         </form>

//         {/* Add Sector Modal */}
//         {showModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
//             <div className="bg-white rounded-lg p-6 w-80 relative" onClick={(e) => e.stopPropagation()}>
//               <button
//                 className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(false)}
//               >
//                 <X size={14} />
//               </button>
//               <div className="flex flex-col items-center">
//                 <h2 className="text-xl font-bold mb-3">Add New Sector</h2>
//                 <form onSubmit={(e) => {
//                   e.preventDefault();
//                   handleAddSector();
//                 }} className="w-full">
//                   <TextField
//                     autoComplete="off"
//                     label="New Sector"
//                     fullWidth
//                     size="small"
//                     value={newSector}
//                     onChange={(e) => setNewSector(e.target.value)}
//                     variant="outlined"
//                     className={shouldShake("sector")}
//                     error={!!sectorError}
//                     helperText={sectorError}
//                     sx={{ mb: 2 }}
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         e.preventDefault();
//                         handleAddSector();
//                       }
//                     }}
//                   />
//                   <div className="flex justify-center w-full">
//                     <Button
//                       type="submit"
//                       variant="contained"
//                       sx={{
//                         py: 1,
//                         background: 'linear-gradient(135deg, #3b82f6, #6d28d9)',
//                         color: '#fff',
//                         fontWeight: 'bold',
//                         borderRadius: 2,
//                         width: '100%',
//                         maxWidth: '120px'
//                       }}
//                       onClick={handleAddSector}
//                     >
//                       Add
//                     </Button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Success Modal */}
//         {showSuccessModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-xl shadow-md p-6 w-80 relative">
//               <button onClick={closeSuccessModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Congratulations!</h2>
//                 <p className="text-gray-600 text-center text-sm">
//                   Your project has been successfully submitted.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Update Modal */}
//         {showUpdateModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 w-80 relative">
//               <button onClick={closeUpdateModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <Edit className="w-10 h-10 text-blue-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Successfully Updated!</h2>
//                 <p className="text-gray-600 text-center text-sm">Your project has been successfully updated.</p>
//               </div>
//             </div>
//           </div>
//         )}
        
//       </Paper>
//     </Box>
//   );
// };

// export default AddProject1;



// import React, { useState, useEffect, useRef } from "react";
// import axios from 'axios';
// import "react-datepicker/dist/react-datepicker.css";
// import { Country, State, City } from "country-state-city";
// import { Plus, Upload, X, CheckCircle, Trash, Edit, FilePlus, MapPinned, Loader2 } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { TextField, MenuItem, Button, Box, Typography, Grid, Paper, Divider, IconButton } from '@mui/material';
// import axiosInstance from "../axiosInstance";
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const AddProject1 = ({ editingProject, onClose , handleReloadPrjects}) => {
//   const [formData, setFormData] = useState({
//     project_name: "",
//     sector: "",
//     cost: "",
//     status: "",
//     country: "",
//     state: "",
//     city: "",
//     startDate: null,
//     endDate: null,
//     contractor: "",
//     districtMagistrate: '',
//     population: '',
//     registrarOffice: '',
//     circleRate: '',
//     kmlFile: null,
//     documentFile: null,
//   });

//   const [sectors, setSectors] = useState(["Commercial", "Construction", "Education", "Factory", "Highways", "Infrastructure", "Mall", "Railways", "Residential", "Retail"]);
//   const [newSector, setNewSector] = useState("");
//   const [showModal, setShowModal] = useState(false);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [shakeFields, setShakeFields] = useState([]);
//   const [sectorError, setSectorError] = useState("");
//   const [fileName, setFileName] = useState('');
//   const [kmlFileName, setKmlFileName] = useState('');
//   const [message, setMessage] = useState("");
//   const [isUploading, setIsUploading] = useState(false);
//   const [kmlFiles, setKmlFiles] = useState([]);
//   const [showRequiredError, setShowRequiredError] = useState(false);
//   const [errorFields, setErrorFields] = useState([]);
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const companyId = '67ce66294f28f85e4ff91e2c';
//   const navigate = useNavigate();
//   const formRef = useRef(null);

//   useEffect(() => {
//     if (editingProject) {
//       setFormData({
//         ...editingProject,
//         startDate: editingProject.startDate ? new Date(editingProject.startDate) : null,
//         endDate: editingProject.endDate ? new Date(editingProject.endDate) : null,
//       });
//       if (editingProject.kmlFile) {
//         setKmlFileName(editingProject.kmlFile.name || 'KML file uploaded');
//       }
//     }
//   }, [editingProject]);

//   useEffect(() => {
//     if (errorFields.length > 0) {
//       const timer = setTimeout(() => {
//         setErrorFields([]);
//       }, 2000);
//       return () => clearTimeout(timer);
//     }
//   }, [errorFields]);

//   const handleAddSector = () => {
//     if (newSector.trim() && !sectors.includes(newSector.trim())) {
//       setSectors((prev) => [...prev, newSector.trim()]);
//       setNewSector("");
//       setShowModal(false);
//       setSectorError("");
//     } else {
//       setSectorError("Sector already exists or invalid input!");
//       setShakeFields(["sector"]);
//     }
//   };

//   const handleDeleteSector = (sectorToDelete) => {
//     setSectors((prev) => prev.filter((sector) => sector !== sectorToDelete));
//     if (formData.sector === sectorToDelete) {
//       setFormData({ ...formData, sector: "" });
//     }
//   };

//   const getCountryCode = (countryName) => {
//     const country = Country.getAllCountries().find((c) => c.name === countryName);
//     return country ? country.isoCode : "";
//   };

//   const getStateCode = (stateName, countryCode) => {
//     const state = State.getStatesOfCountry(countryCode).find((s) => s.name === stateName);
//     return state ? state.isoCode : "";
//   };

//   const [document, setDocument] = useState("");

//   const handleChange = (e) => {
//     const { name, value, files } = e.target;
//     if (name === "kmlFile") {
//       const selectedFiles = Array.from(files);
//       setKmlFiles(selectedFiles);
//       setFormData({
//         ...formData,
//         [name]: selectedFiles[0],
//       });
//       if (selectedFiles.length > 0) {
//         setKmlFileName(
//           selectedFiles.length > 1
//             ? `${selectedFiles.length} files selected`
//             : selectedFiles[0].name
//         );
//       } else {
//         setKmlFileName('');
//       }
//     } else if (name === "documentFile") {
//       const file = files[0];
//       setDocument(file)
//       setFormData({
//         ...formData,
//         [name]: file,
//       });
//       setFileName(file ? file.name : '');
//     } else if (name === "country") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         state: "",
//         city: "",
//       });
//     } else if (name === "state") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         city: "",
//       });
//     } else {
//       setFormData({
//         ...formData,
//         [name]: value,
//       });
//     }
//   };

//   const handleDateChange = (date, field) => {
//     setFormData({
//       ...formData,
//       [field]: date,
//     });
//   };

//   const validateForm = () => {
//     const requiredFields = [
//       'project_name',
//       'sector',
//       'cost',
//       'status',
//       'country',
//       'state',
//       'city'
//     ];
    
//     const emptyFields = requiredFields.filter(field => !formData[field]);
    
//     if (emptyFields.length > 0 || (!formData.kmlFile && !formData.documentFile)) {
//       setShakeFields(emptyFields);
//       setErrorFields(emptyFields);
//       setShowRequiredError(true);
//       // toast.error("Please fill all required fields");
      
//       setTimeout(() => {
//         setShowRequiredError(false);
//         setErrorFields([]);
//       }, 2000);
      
//       return false;
//     }
    
//     setShowRequiredError(false);
//     return true;
//   };

//   const handleSubmit = async (payloadurl) => {
//     if (!validateForm()) {
//       setIsSubmitting(false); // Ensure loader is turned off if validation fails
//       return;
//     }
    
//     try {
//       const payload = {
//         project_name: formData.project_name,
//         sector: formData.sector,
//         cost: formData.cost,
//         status: formData.status,
//         country: formData.country,
//         state: formData.state,
//         city: formData.city,
//         startDate: formData.startDate?.toISOString().split("T")[0],
//         endDate: formData.endDate?.toISOString().split("T")[0],
//         contractor: formData.contractor,
//         districtMagistrate: formData.districtMagistrate,
//         population: formData.population,
//         registrarOffice: formData.registrarOffice,
//         circleRate: formData.circleRate,
//         kml: payloadurl.kmlUrls ? payloadurl.kmlUrls : [],
//         documentFile: payloadurl.documentUrl ? payloadurl.documentUrl : [],
//       };

//       if (editingProject) {
//         const response = await axiosInstance.put(
//           `/project/updateProject`,
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'Content-Type': 'application/json',
//               'x-project-id': editingProject._id
//             },
//           }
//         );
//         console.log('Project updated successfully:', response.data);
//         setShowUpdateModal(true);
//       } else {
//         const response = await axiosInstance.post(
//           '/project/createProject',
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'x-auth-token': localStorage.getItem('token'),
//               'Content-Type': 'application/json',
//             },
//           }
//         );
//         console.log('Project created successfully:', response.data);
//         setShowSuccessModal(true);
//       }
//     } catch (error) {
//       console.error('Error submitting project:', error.response?.data || error.message);
//       toast.error('Error submitting project');
//     } finally {
//       handleReloadPrjects()
//       setIsSubmitting(false);
//     }
//   };

//   const closeSuccessModal = () => {
//     setShowSuccessModal(false);
//     onClose();
//   };

//   const closeUpdateModal = () => {
//     setShowUpdateModal(false);
//     onClose();
//   };

//   const shouldShake = (fieldName) => {
//     return shakeFields.includes(fieldName) ? "animate-shake" : "";
//   };

//   const handleKmlUpload = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true); // Show loader immediately on click
    
//     if (!validateForm()) {
//       setIsSubmitting(false);
//       return;
//     }
    
//     if (kmlFiles.length === 0 && !document) {
//       setMessage("No files selected!");
//       await handleSubmit({});
//       return;
//     }

//     setIsUploading(true);
//     setMessage("Uploading files...");

//     try {
//       let uploadedKmlUrls = [];
//       let documentUrl = null;

//       if (kmlFiles.length > 0) {
//         for (const file of kmlFiles) {
//           const formData = new FormData();
//           formData.append("file", file);
//           formData.append("upload_preset", "ml_default");

//           const response = await axios.post(
//             "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//             formData
//           );
//           uploadedKmlUrls.push(response.data.secure_url);
//         }
//       }

//       if (document) {
//         const formData = new FormData();
//         formData.append("file", document);
//         formData.append("upload_preset", "ml_default");

//         const response = await axios.post(
//           "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//           formData
//         );
//         documentUrl = response.data.secure_url;
//       }

//       const payload = {
//         kmlUrls: uploadedKmlUrls.length > 0 ? uploadedKmlUrls : null,
//         documentUrl: documentUrl || null,
//       };

//       await handleSubmit(payload);
//       // toast.success("Files uploaded successfully!");
//     } catch (error) {
//       toast.error("Upload failed");
//       console.error("Upload Error:", error);
//     } finally {
//       setIsUploading(false);
//       setKmlFiles([]);
//     }
//   };

//   const renderLabel = (fieldName, label) => {
//     const requiredFields = ['project_name', 'sector', 'cost', 'status', 'country', 'state', 'city'];
//     const isRequired = requiredFields.includes(fieldName);
//     const isMissing = errorFields.includes(fieldName);
    
//     return (
//       <span>
//         {label}
//         {isRequired && <span style={{ 
//           color: isMissing ? 'red' : 'inherit',
//           transition: 'color 0.5s ease-out'
//         }}>*</span>}
//       </span>
//     );
//   };

//   return (
//     <Box display="flex" justifyContent="center" alignItems="flex-start" p={2} ref={formRef}>
//       <ToastContainer position="top-right" autoClose={5000} />
//       <Paper elevation={3} sx={{ p: 3, borderRadius: 4, maxWidth: 750, width: '100%', background: '#fff', mt: 0, position: 'relative' }}>
//         <button
//           className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//           onClick={onClose}
//         >
//           <X size={14} />
//         </button>

//         <Typography variant="h5" align="center" fontWeight="bold" color="primary" gutterBottom>
//           {editingProject ? "Edit Project" : "Add Project"}
//         </Typography>
//         <Divider sx={{ my: 2 }} />
//         <form onSubmit={(e) => handleKmlUpload(e)} autoComplete="off">
//           <Grid container spacing={2}>
//             {/* Project Name */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("project_name", "Project Name")}
//                 fullWidth
//                 size="small"
//                 name="project_name"
//                 value={formData.project_name}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("project_name")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("project_name") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Sector */}
//             <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
//               <TextField
//                 label={renderLabel("sector", "Sector")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="sector"
//                 value={formData.sector}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("sector")}
//                 SelectProps={{
//                   renderValue: (selected) => selected,
//                 }}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("sector") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Sector</MenuItem>
//                 {sectors.map((sector, index) => (
//                   <MenuItem key={index} value={sector}>
//                     <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
//                       {sector}
//                       {formData.sector !== sector && (
//                         <IconButton
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleDeleteSector(sector);
//                           }}
//                           size="small"
//                         >
//                           <X size={14} />
//                         </IconButton>
//                       )}
//                     </Box>
//                   </MenuItem>
//                 ))}
//               </TextField>
            
//               <button
//                 className="absolute top-1 -right-3 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(true)}
//               >
//                 <Plus size={18} />
//               </button>
//             </Grid>

//             {/* Cost */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("cost", "Cost (INR)")}
//                 type="number"
//                 fullWidth
//                 size="small"
//                 name="cost"
//                 value={formData.cost}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("cost")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("cost") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Status */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("status", "Status")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="status"
//                 value={formData.status}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("status")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("status") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Status</MenuItem>
//                 <MenuItem value="Conceptual">Conceptual</MenuItem>
//                 <MenuItem value="DPR">DPR</MenuItem>
//                 <MenuItem value="Tender">Tender</MenuItem>
//                 <MenuItem value="Under-Construction">Under-Construction</MenuItem>
//                 <MenuItem value="Constructed">Constructed</MenuItem>
//               </TextField>
//             </Grid>

//             {/* Country */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("country", "Country")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="country"
//                 value={formData.country}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("country")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("country") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Country</MenuItem>
//                 {Country.getAllCountries().map((country) => (
//                   <MenuItem key={country.isoCode} value={country.name}>
//                     {country.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* State */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("state", "State")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="state"
//                 value={formData.state}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("state")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("state") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select State</MenuItem>
//                 {State.getStatesOfCountry(getCountryCode(formData.country)).map((state) => (
//                   <MenuItem key={state.isoCode} value={state.name}>
//                     {state.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* City */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("city", "City")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="city"
//                 value={formData.city}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("city")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("city") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select City</MenuItem>
//                 {City.getCitiesOfState(getCountryCode(formData.country), getStateCode(formData.state, getCountryCode(formData.country))).map((city) => (
//                   <MenuItem key={city.name} value={city.name}>
//                     {city.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* Contractor */}
//             <Grid item xs={12} md={6}>
//               <TextField label="Contractor" fullWidth size="small" name="contractor" value={formData.contractor} onChange={handleChange} variant="outlined" />
//             </Grid>

//             {/* districtMagistrate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="District Magistrate"
//                 fullWidth
//                 size="small"
//                 name="districtMagistrate"
//                 value={formData.districtMagistrate}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* population */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Population"
//                 fullWidth
//                 size="small"
//                 type="number"
//                 name="population"
//                 value={formData.population}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* registrarOffice */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Registrar Office"
//                 fullWidth
//                 size="small"
//                 name="registrarOffice"
//                 value={formData.registrarOffice}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* circleRate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Circle Rate (INR)"
//                 fullWidth
//                 size="small"
//                 name="circleRate"
//                 type="number"
//                 value={formData.circleRate}
//                 onChange={handleChange}
//                 variant="outlined"
                
//               />
//             </Grid>

//             {/* Start Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Start Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="startDate"
//                 value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "startDate")}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* Finish Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Finish Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="endDate"
//                 value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "endDate")}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* KML File Upload */}
//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{ 
//                   py: 1, 
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6', 
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2, 
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out'
//                 }}
//               >
//                 <MapPinned size={18} style={{ marginRight: '6px' }} />
//                 Upload KML
//                 <input type="file" name="kmlFile" accept=".kml, .kmz" multiple onChange={handleChange} disabled={isUploading} className="absolute inset-0 opacity-0 cursor-pointer" />
//               </Button>
//               {formData.kmlFile && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {formData.kmlFile.name}
//                 </Typography>
//               )}
//             </Grid>

//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{
//                   py: 1,
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6',
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6',
//                   fontWeight: 'bold',
//                   borderRadius: 2,
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out'
//                 }}
//               >
//                 <FilePlus size={18} style={{ marginRight: '6px' }} />
//                 Upload Document
//                 <input
//                   type="file"
//                   name="documentFile"
//                   onChange={handleChange}
//                   style={{ display: 'none' }}
//                   accept=".pdf,.doc,.docx"
//                 />
//               </Button>
//               {fileName && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {fileName}
//                 </Typography>
//               )}
//             </Grid>

//             {/* Submit Button */}
//             <Grid item xs={12}>
//               <Button
//                 type="submit"
//                 fullWidth
//                 variant="contained"
//                 disabled={isSubmitting}
//                 sx={{ 
//                   py: 1, 
//                   background: 'linear-gradient(135deg, #3b82f6, #6d28d9)', 
//                   color: '#fff', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2,
//                   '&:disabled': {
//                     background: '#e0e0e0',
//                     color: '#9e9e9e'
//                   }
//                 }}
//               >
//                 {isSubmitting ? (
//                   <>
//                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
//                     Submitting...
//                   </>
//                 ) : (
//                   'Submit'
//                 )}
//               </Button>
//             </Grid>
//           </Grid>
//         </form>

//         {/* Add Sector Modal */}
//         {showModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
//             <div className="bg-white rounded-lg p-6 w-80 relative" onClick={(e) => e.stopPropagation()}>
//               <button
//                 className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(false)}
//               >
//                 <X size={14} />
//               </button>
//               <div className="flex flex-col items-center">
//                 <h2 className="text-xl font-bold mb-3">Add New Sector</h2>
//                 <form onSubmit={(e) => {
//                   e.preventDefault();
//                   handleAddSector();
//                 }} className="w-full">
//                   <TextField
//                     autoComplete="off"
//                     label="New Sector"
//                     fullWidth
//                     size="small"
//                     value={newSector}
//                     onChange={(e) => setNewSector(e.target.value)}
//                     variant="outlined"
//                     className={shouldShake("sector")}
//                     error={!!sectorError}
//                     helperText={sectorError}
//                     sx={{ mb: 2 }}
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         e.preventDefault();
//                         handleAddSector();
//                       }
//                     }}
//                   />
//                   <div className="flex justify-center w-full">
//                     <Button
//                       type="submit"
//                       variant="contained"
//                       sx={{
//                         py: 1,
//                         background: 'linear-gradient(135deg, #3b82f6, #6d28d9)',
//                         color: '#fff',
//                         fontWeight: 'bold',
//                         borderRadius: 2,
//                         width: '100%',
//                         maxWidth: '120px'
//                       }}
//                       onClick={handleAddSector}
//                     >
//                       Add
//                     </Button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Success Modal */}
//         {showSuccessModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-xl shadow-md p-6 w-80 relative">
//               <button onClick={closeSuccessModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Congratulations!</h2>
//                 <p className="text-gray-600 text-center text-sm">
//                   Your project has been successfully submitted.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Update Modal */}
//         {showUpdateModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 w-80 relative">
//               <button onClick={closeUpdateModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <Edit className="w-10 h-10 text-blue-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Successfully Updated!</h2>
//                 <p className="text-gray-600 text-center text-sm">Your project has been successfully updated.</p>
//               </div>
//             </div>
//           </div>
//         )}
        
//       </Paper>
//     </Box>
//   );
// };

// export default AddProject1;

// use this code also if have any issue. form without loader 
// import React, { useState, useEffect, useRef } from "react";
// import axios from 'axios';
// import "react-datepicker/dist/react-datepicker.css";
// import { Country, State, City } from "country-state-city";
// import { Plus, Upload, X, CheckCircle, Trash, Edit, FilePlus, MapPinned } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { TextField, MenuItem, Button, Box, Typography, Grid, Paper, Divider, IconButton } from '@mui/material';
// import axiosInstance from "../axiosInstance";
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const AddProject1 = ({ editingProject, onClose }) => {
//   const [formData, setFormData] = useState({
//     project_name: "",
//     sector: "",
//     cost: "",
//     status: "",
//     country: "",
//     state: "",
//     city: "",
//     startDate: null,
//     endDate: null,
//     contractor: "",
//     districtMagistrate: '',
//     population: '',
//     registrarOffice: '',
//     circleRate: '',
//     kmlFile: null,
//     documentFile: null,
//   });

//   const [sectors, setSectors] = useState(["Commercial", "Construction", "Education", "Factory", "Highways", "Infrastructure", "Mall", "Railways", "Residential", "Retail"]);
//   const [newSector, setNewSector] = useState("");
//   const [showModal, setShowModal] = useState(false);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [shakeFields, setShakeFields] = useState([]);
//   const [sectorError, setSectorError] = useState("");
//   const [fileName, setFileName] = useState('');
//   const [kmlFileName, setKmlFileName] = useState('');
//   const [message, setMessage] = useState("");
//   const [isUploading, setIsUploading] = useState(false);
//   const [kmlFiles, setKmlFiles] = useState([]);
//   const [showRequiredError, setShowRequiredError] = useState(false);
//   const [errorFields, setErrorFields] = useState([]);

//   const companyId = '67ce66294f28f85e4ff91e2c';
//   const navigate = useNavigate();
//   const formRef = useRef(null);

//   useEffect(() => {
//     if (editingProject) {
//       setFormData({
//         ...editingProject,
//         startDate: editingProject.startDate ? new Date(editingProject.startDate) : null,
//         endDate: editingProject.endDate ? new Date(editingProject.endDate) : null,
//       });
//       if (editingProject.kmlFile) {
//         setKmlFileName(editingProject.kmlFile.name || 'KML file uploaded');
//       }
//     }
//   }, [editingProject]);

//   useEffect(() => {
//     if (errorFields.length > 0) {
//       const timer = setTimeout(() => {
//         setErrorFields([]);
//       }, 2000);
//       return () => clearTimeout(timer);
//     }
//   }, [errorFields]);

//   const handleAddSector = () => {
//     if (newSector.trim() && !sectors.includes(newSector.trim())) {
//       setSectors((prev) => [...prev, newSector.trim()]);
//       setNewSector("");
//       setShowModal(false);
//       setSectorError("");
//     } else {
//       setSectorError("Sector already exists or invalid input!");
//       setShakeFields(["sector"]);
//     }
//   };

//   const handleDeleteSector = (sectorToDelete) => {
//     setSectors((prev) => prev.filter((sector) => sector !== sectorToDelete));
//     if (formData.sector === sectorToDelete) {
//       setFormData({ ...formData, sector: "" });
//     }
//   };

//   const getCountryCode = (countryName) => {
//     const country = Country.getAllCountries().find((c) => c.name === countryName);
//     return country ? country.isoCode : "";
//   };

//   const getStateCode = (stateName, countryCode) => {
//     const state = State.getStatesOfCountry(countryCode).find((s) => s.name === stateName);
//     return state ? state.isoCode : "";
//   };

//   const [document, setDocument] = useState("");

//   const handleChange = (e) => {
//     const { name, value, files } = e.target;
//     if (name === "kmlFile") {
//       const selectedFiles = Array.from(files);
//       setKmlFiles(selectedFiles);
//       setFormData({
//         ...formData,
//         [name]: selectedFiles[0],
//       });
//       if (selectedFiles.length > 0) {
//         setKmlFileName(
//           selectedFiles.length > 1
//             ? `${selectedFiles.length} files selected`
//             : selectedFiles[0].name
//         );
//       } else {
//         setKmlFileName('');
//       }
//     } else if (name === "documentFile") {
//       const file = files[0];
//       setDocument(file)
//       setFormData({
//         ...formData,
//         [name]: file,
//       });
//       setFileName(file ? file.name : '');
//     } else if (name === "country") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         state: "",
//         city: "",
//       });
//     } else if (name === "state") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         city: "",
//       });
//     } else {
//       setFormData({
//         ...formData,
//         [name]: value,
//       });
//     }
//   };

//   const handleDateChange = (date, field) => {
//     setFormData({
//       ...formData,
//       [field]: date,
//     });
//   };

//   const validateForm = () => {
//     const requiredFields = [
//       'project_name',
//       'sector',
//       'cost',
//       'status',
//       'country',
//       'state',
//       'city'
//     ];
    
//     const emptyFields = requiredFields.filter(field => !formData[field]);
    
//     if (emptyFields.length > 0 || (!formData.kmlFile && !formData.documentFile)) {
//       setShakeFields(emptyFields);
//       setErrorFields(emptyFields);
//       setShowRequiredError(true);
//       toast.error("Please fill all required fields");
      
//       // Auto-hide error after 2 seconds
//       setTimeout(() => {
//         setShowRequiredError(false);
//         setErrorFields([]);
//       }, 2000);
      
//       return false;
//     }
    
//     setShowRequiredError(false);
//     return true;
//   };

//   const handleSubmit = async (payloadurl) => {
//     if (!validateForm()) {
//       return;
//     }
    
//     try {
//       const payload = {
//         project_name: formData.project_name,
//         sector: formData.sector,
//         cost: formData.cost,
//         status: formData.status,
//         country: formData.country,
//         state: formData.state,
//         city: formData.city,
//         startDate: formData.startDate?.toISOString().split("T")[0],
//         endDate: formData.endDate?.toISOString().split("T")[0],
//         contractor: formData.contractor,
//         districtMagistrate: formData.districtMagistrate,
//         population: formData.population,
//         registrarOffice: formData.registrarOffice,
//         circleRate: formData.circleRate,
//         kml: payloadurl.kmlUrls ? payloadurl.kmlUrls : [],
//         documentFile: payloadurl.documentUrl ? payloadurl.documentUrl : [],
//       };

//       if (editingProject) {
//         const response = await axiosInstance.put(
//           `/project/updateProject`,
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'Content-Type': 'application/json',
//               'x-project-id': editingProject._id
//             },
//           }
//         );
//         console.log('Project updated successfully:', response.data);
//         setShowUpdateModal(true);
//       } else {
//         const response = await axiosInstance.post(
//           '/project/createProject',
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'x-auth-token': localStorage.getItem('token'),
//               'Content-Type': 'application/json',
//             },
//           }
//         );
//         console.log('Project created successfully:', response.data);
//         setShowSuccessModal(true);
//       }
//     } catch (error) {
//       console.error('Error submitting project:', error.response?.data || error.message);
//       toast.error('Error submitting project');
//     }
//   };

//   const closeSuccessModal = () => {
//     setShowSuccessModal(false);
//     onClose();
//   };

//   const closeUpdateModal = () => {
//     setShowUpdateModal(false);
//     onClose();
//   };

//   const shouldShake = (fieldName) => {
//     return shakeFields.includes(fieldName) ? "animate-shake" : "";
//   };

//   const handleKmlUpload = async (e) => {
//     e.preventDefault();
    
//     if (!validateForm()) {
//       return;
//     }
    
//     if (kmlFiles.length === 0 && !document) {
//       setMessage("No files selected!");
//       await handleSubmit({});
//       return;
//     }

//     setIsUploading(true);
//     setMessage("Uploading files...");

//     try {
//       let uploadedKmlUrls = [];
//       let documentUrl = null;

//       if (kmlFiles.length > 0) {
//         for (const file of kmlFiles) {
//           const formData = new FormData();
//           formData.append("file", file);
//           formData.append("upload_preset", "ml_default");

//           const response = await axios.post(
//             "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//             formData
//           );
//           uploadedKmlUrls.push(response.data.secure_url);
//         }
//       }

//       if (document) {
//         const formData = new FormData();
//         formData.append("file", document);
//         formData.append("upload_preset", "ml_default");

//         const response = await axios.post(
//           "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//           formData
//         );
//         documentUrl = response.data.secure_url;
//       }

//       const payload = {
//         kmlUrls: uploadedKmlUrls.length > 0 ? uploadedKmlUrls : null,
//         documentUrl: documentUrl || null,
//       };

//       await handleSubmit(payload);
//       toast.success("Files uploaded successfully!");
//     } catch (error) {
//       toast.error("Upload failed");
//       console.error("Upload Error:", error);
//     } finally {
//       setIsUploading(false);
//       setKmlFiles([]);
//     }
//   };

//   // Function to render label with red asterisk for required fields
//   const renderLabel = (fieldName, label) => {
//     const requiredFields = ['project_name', 'sector', 'cost', 'status', 'country', 'state', 'city'];
//     const isRequired = requiredFields.includes(fieldName);
//     const isMissing = errorFields.includes(fieldName);
    
//     return (
//       <span>
//         {label}
//         {isRequired && <span style={{ 
//           color: isMissing ? 'red' : 'inherit',
//           transition: 'color 0.5s ease-out'
//         }}>*</span>}
//       </span>
//     );
//   };

//   return (
//     <Box display="flex" justifyContent="center" alignItems="flex-start" p={2} ref={formRef}>
//       <ToastContainer position="top-right" autoClose={5000} />
//       <Paper elevation={3} sx={{ p: 3, borderRadius: 4, maxWidth: 750, width: '100%', background: '#fff', mt: 0, position: 'relative' }}>
//         <button
//           className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//           onClick={onClose}
//         >
//           <X size={14} />
//         </button>

//         <Typography variant="h5" align="center" fontWeight="bold" color="primary" gutterBottom>
//           {editingProject ? "Edit Project" : "Add Project"}
//         </Typography>
//         <Divider sx={{ my: 2 }} />
//         <form onSubmit={(e) => handleKmlUpload(e)} autoComplete="off">
//           <Grid container spacing={2}>
//             {/* Project Name */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("project_name", "Project Name")}
//                 fullWidth
//                 size="small"
//                 name="project_name"
//                 value={formData.project_name}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("project_name")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("project_name") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Sector */}
//             <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
//               <TextField
//                 label={renderLabel("sector", "Sector")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="sector"
//                 value={formData.sector}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("sector")}
//                 SelectProps={{
//                   renderValue: (selected) => selected,
//                 }}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("sector") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Sector</MenuItem>
//                 {sectors.map((sector, index) => (
//                   <MenuItem key={index} value={sector}>
//                     <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
//                       {sector}
//                       {formData.sector !== sector && (
//                         <IconButton
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleDeleteSector(sector);
//                           }}
//                           size="small"
//                         >
//                           <X size={14} />
//                         </IconButton>
//                       )}
//                     </Box>
//                   </MenuItem>
//                 ))}
//               </TextField>
            
//               <button
//                 className="absolute top-1 -right-3 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(true)}
//               >
//                 <Plus size={18} />
//               </button>
//             </Grid>

//             {/* Cost */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("cost", "Cost (INR)")}
//                 type="number"
//                 fullWidth
//                 size="small"
//                 name="cost"
//                 value={formData.cost}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("cost")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("cost") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Status */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("status", "Status")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="status"
//                 value={formData.status}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("status")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("status") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Status</MenuItem>
//                 <MenuItem value="Conceptual">Conceptual</MenuItem>
//                 <MenuItem value="DPR">DPR</MenuItem>
//                 <MenuItem value="Tender">Tender</MenuItem>
//                 <MenuItem value="Under-Construction">Under-Construction</MenuItem>
//                 <MenuItem value="Constructed">Constructed</MenuItem>
//               </TextField>
//             </Grid>

//             {/* Country */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("country", "Country")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="country"
//                 value={formData.country}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("country")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("country") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select Country</MenuItem>
//                 {Country.getAllCountries().map((country) => (
//                   <MenuItem key={country.isoCode} value={country.name}>
//                     {country.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* State */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("state", "State")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="state"
//                 value={formData.state}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("state")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("state") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select State</MenuItem>
//                 {State.getStatesOfCountry(getCountryCode(formData.country)).map((state) => (
//                   <MenuItem key={state.isoCode} value={state.name}>
//                     {state.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* City */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label={renderLabel("city", "City")}
//                 select
//                 fullWidth
//                 size="small"
//                 name="city"
//                 value={formData.city}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("city")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: errorFields.includes("city") ? 'red' : '',
//                       transition: 'border-color 0.5s ease-out'
//                     },
//                   },
//                 }}
//               >
//                 <MenuItem value="">Select City</MenuItem>
//                 {City.getCitiesOfState(getCountryCode(formData.country), getStateCode(formData.state, getCountryCode(formData.country))).map((city) => (
//                   <MenuItem key={city.name} value={city.name}>
//                     {city.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* Contractor */}
//             <Grid item xs={12} md={6}>
//               <TextField label="Contractor" fullWidth size="small" name="contractor" value={formData.contractor} onChange={handleChange} variant="outlined" />
//             </Grid>

//             {/* districtMagistrate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="District Magistrate"
//                 fullWidth
//                 size="small"
//                 name="districtMagistrate"
//                 value={formData.districtMagistrate}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* population */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Population"
//                 fullWidth
//                 size="small"
//                 type="number"
//                 name="population"
//                 value={formData.population}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* registrarOffice */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Registrar Office"
//                 fullWidth
//                 size="small"
//                 name="registrarOffice"
//                 value={formData.registrarOffice}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* circleRate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Circle Rate (INR)"
//                 fullWidth
//                 size="small"
//                 name="circleRate"
//                 type="number"
//                 value={formData.circleRate}
//                 onChange={handleChange}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* Start Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Start Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="startDate"
//                 value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "startDate")}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* Finish Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Finish Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="endDate"
//                 value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "endDate")}
//                 variant="outlined"
//               />
//             </Grid>

//             {/* KML File Upload */}
//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{ 
//                   py: 1, 
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6', 
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2, 
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out'
//                 }}
//               >
//                 <MapPinned size={18} style={{ marginRight: '6px' }} />
//                 Upload KML
//                 <input type="file" name="kmlFile" accept=".kml, .kmz" multiple onChange={handleChange} disabled={isUploading} className="absolute inset-0 opacity-0 cursor-pointer" />
//               </Button>
//               {formData.kmlFile && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {formData.kmlFile.name}
//                 </Typography>
//               )}
//             </Grid>

//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{
//                   py: 1,
//                   border: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6',
//                   color: showRequiredError && !formData.kmlFile && !formData.documentFile 
//                     ? 'red' 
//                     : '#3b82f6',
//                   fontWeight: 'bold',
//                   borderRadius: 2,
//                   position: 'relative',
//                   transition: 'border-color 0.5s ease-out, color 0.5s ease-out'
//                 }}
//               >
//                 <FilePlus size={18} style={{ marginRight: '6px' }} />
//                 Upload Document
//                 <input
//                   type="file"
//                   name="documentFile"
//                   onChange={handleChange}
//                   style={{ display: 'none' }}
//                   accept=".pdf,.doc,.docx"
//                 />
//               </Button>
//               {fileName && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {fileName}
//                 </Typography>
//               )}
//             </Grid>

//             {/* Submit Button */}
//             <Grid item xs={12}>
//               <Button
//                 type="submit"
//                 fullWidth
//                 variant="contained"
//                 sx={{ py: 1, background: 'linear-gradient(135deg, #3b82f6, #6d28d9)', color: '#fff', fontWeight: 'bold', borderRadius: 2 }}
//               >
//                 Submit
//               </Button>
//             </Grid>
//           </Grid>
//         </form>

//         {/* Add Sector Modal */}
//         {showModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
//             <div className="bg-white rounded-lg p-6 w-80 relative" onClick={(e) => e.stopPropagation()}>
//               <button
//                 className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(false)}
//               >
//                 <X size={14} />
//               </button>
//               <div className="flex flex-col items-center">
//                 <h2 className="text-xl font-bold mb-3">Add New Sector</h2>
//                 <form onSubmit={(e) => {
//                   e.preventDefault();
//                   handleAddSector();
//                 }} className="w-full">
//                   <TextField
//                     autoComplete="off"
//                     label="New Sector"
//                     fullWidth
//                     size="small"
//                     value={newSector}
//                     onChange={(e) => setNewSector(e.target.value)}
//                     variant="outlined"
//                     className={shouldShake("sector")}
//                     error={!!sectorError}
//                     helperText={sectorError}
//                     sx={{ mb: 2 }}
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         e.preventDefault();
//                         handleAddSector();
//                       }
//                     }}
//                   />
//                   <div className="flex justify-center w-full">
//                     <Button
//                       type="submit"
//                       variant="contained"
//                       sx={{
//                         py: 1,
//                         background: 'linear-gradient(135deg, #3b82f6, #6d28d9)',
//                         color: '#fff',
//                         fontWeight: 'bold',
//                         borderRadius: 2,
//                         width: '100%',
//                         maxWidth: '120px'
//                       }}
//                       onClick={handleAddSector}
//                     >
//                       Add
//                     </Button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Success Modal */}
//         {showSuccessModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-xl shadow-md p-6 w-80 relative">
//               <button onClick={closeSuccessModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Congratulations!</h2>
//                 <p className="text-gray-600 text-center text-sm">
//                   Your project has been successfully submitted.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Update Modal */}
//         {showUpdateModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 w-80 relative">
//               <button onClick={closeUpdateModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <Edit className="w-10 h-10 text-blue-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Successfully Updated!</h2>
//                 <p className="text-gray-600 text-center text-sm">Your project has been successfully updated.</p>
//               </div>
//             </div>
//           </div>
//         )}
//       </Paper>
//     </Box>
//   );
// };

// export default AddProject1;


// import React, { useState, useEffect, useRef } from "react";
// import axios from 'axios';
// import "react-datepicker/dist/react-datepicker.css";
// import { Country, State, City } from "country-state-city";
// import { Plus, Upload, X, CheckCircle, Trash, Edit, FilePlus, MapPinned } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { TextField, MenuItem, Button, Box, Typography, Grid, Paper, Divider, IconButton, InputAdornment } from '@mui/material';
// import axiosInstance from "../axiosInstance";

// const AddProject1 = ({ editingProject, onClose }) => {
//   const [formData, setFormData] = useState({
//     project_name: "",
//     sector: "",
//     cost: "",
//     status: "",
//     country: "",
//     state: "",
//     city: "",
//     startDate: null,
//     endDate: null,
//     contractor: "",
//     districtMagistrate: '',
//     population: '',
//     registrarOffice: '',
//     circleRate: '',
//     kmlFile: null,
//     documentFile: null,
//   });

//   const [sectors, setSectors] = useState(["Commercial", "Construction", "Education", "Factory", "Highways", "Infrastructure", "Mall", "Railways", "Residential", "Retail"]);

//   const [newSector, setNewSector] = useState("");
//   const [showModal, setShowModal] = useState(false);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [shakeFields, setShakeFields] = useState([]);
//   const [sectorError, setSectorError] = useState("");
//   const [fileName, setFileName] = useState('');
//   const [kmlFileName, setKmlFileName] = useState(''); // New state for KML file name
//   const [message, setMessage] = useState("");
//   const [isUploading, setIsUploading] = useState(false);
//   const [kmlFiles, setKmlFiles] = useState([]);

//   const companyId = '67ce66294f28f85e4ff91e2c';
//   const navigate = useNavigate();
//   const formRef = useRef(null);

//   useEffect(() => {
//     if (editingProject) {
//       setFormData({
//         ...editingProject,
//         startDate: editingProject.startDate ? new Date(editingProject.startDate) : null,
//         endDate: editingProject.endDate ? new Date(editingProject.endDate) : null,
//       });
//       if (editingProject.kmlFile) {
//         setKmlFileName(editingProject.kmlFile.name || 'KML file uploaded');
//       }
//     }
//   }, [editingProject]);

//   const handleAddSector = () => {
//     if (newSector.trim() && !sectors.includes(newSector.trim())) {
//       setSectors((prev) => [...prev, newSector.trim()]);
//       setNewSector("");
//       setShowModal(false);
//       setSectorError("");
//     } else {
//       setSectorError("Sector already exists or invalid input!");
//       setShakeFields(["sector"]);
//     }
//   };

//   const handleDeleteSector = (sectorToDelete) => {
//     setSectors((prev) => prev.filter((sector) => sector !== sectorToDelete));
//     if (formData.sector === sectorToDelete) {
//       setFormData({ ...formData, sector: "" });
//     }
//   };

//   const getCountryCode = (countryName) => {
//     const country = Country.getAllCountries().find((c) => c.name === countryName);
//     return country ? country.isoCode : "";
//   };

//   const getStateCode = (stateName, countryCode) => {
//     const state = State.getStatesOfCountry(countryCode).find((s) => s.name === stateName);
//     return state ? state.isoCode : "";
//   };

//   const [document, setDocument] = useState("");

//   const handleChange = (e) => {
//     const { name, value, files } = e.target;
//     if (name === "kmlFile") {
//       const selectedFiles = Array.from(files);
//       setKmlFiles(selectedFiles);
//       setFormData({
//         ...formData,
//         [name]: selectedFiles[0],
//       });
//       // Set KML file name for display
//       if (selectedFiles.length > 0) {
//         setKmlFileName(
//           selectedFiles.length > 1
//             ? `${selectedFiles.length} files selected`
//             : selectedFiles[0].name
//         );
//       } else {
//         setKmlFileName('');
//       }
//     } else if (name === "documentFile") {
//       const file = files[0];
//       setDocument(file)
//       setFormData({
//         ...formData,
//         [name]: file,
//       });
//       setFileName(file ? file.name : '');
//       // console.log(fileName)
//     } else if (name === "country") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         state: "",
//         city: "",
//       });
//     } else if (name === "state") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         city: "",
//       });
//     } else {
//       setFormData({
//         ...formData,
//         [name]: value,
//       });
//     }
//   };

//   const handleDateChange = (date, field) => {
//     setFormData({
//       ...formData,
//       [field]: date,
//     });
//   };

//   const handleSubmit = async (payloadurl) => {
//     console.log("upload kml s", JSON.stringify(formData, null, " "))
//     console.log("upload kml s", JSON.stringify(payloadurl, null, " "))
//     // return ;
//     try {
//       const payload = {
//         project_name: formData.project_name,
//         sector: formData.sector,
//         cost: formData.cost,
//         status: formData.status,
//         country: formData.country,
//         state: formData.state,
//         city: formData.city,
//         startDate: formData.startDate?.toISOString().split("T")[0],
//         endDate: formData.endDate?.toISOString().split("T")[0],
//         contractor: formData.contractor,
//         districtMagistrate: formData.districtMagistrate,
//         population: formData.population,
//         registrarOffice: formData.registrarOffice,
//         circleRate: formData.circleRate,
//         kml: payloadurl.kmlUrls ? payloadurl.kmlUrls : [],
//         documentFile: payloadurl.documentUrl ? payloadurl.documentUrl : [],
//       };

//       if (editingProject) {
//         const response = await axiosInstance.put(
//           `/project/updateProject`,
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'Content-Type': 'application/json',
//               'x-project-id': editingProject._id
//             },
//           }
//         );
//         console.log('Project updated successfully:', response.data);
//         setShowUpdateModal(true);
//       } else {
//         const response = await axiosInstance.post(
//           '/project/createProject',
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'x-auth-token':localStorage.getItem('token'),
//               'Content-Type': 'application/json',
//             },
//           }
//         );
//         console.log('Project created successfully:', response.data);
//         setShowSuccessModal(true);
//       }
//     } catch (error) {
//       console.error('Error submitting project:', error.response?.data || error.message);
//     }
//   };

//   const closeSuccessModal = () => {
//     setShowSuccessModal(false);
//     onClose();
//   };

//   const closeUpdateModal = () => {
//     setShowUpdateModal(false);
//     onClose();
//   };

//   const shouldShake = (fieldName) => {
//     return shakeFields.includes(fieldName) ? "animate-shake" : "";
//   };

//   const handleKmlUpload = async (e) => {
//     e.preventDefault();
//     console.log(`KML files: ${kmlFiles}`);
//     console.log(`Document file: ${document?.name || "No document uploaded"}`);

//     if (kmlFiles.length === 0 && !document) {
//       setMessage("No files selected!");
//       console.log("No files uploaded");
//       await handleSubmit();
//       return;
//     }

//     setIsUploading(true);
//     setMessage("Uploading files...");

//     try {
//       let uploadedKmlUrls = [];
//       let documentUrl = null;

//       // Upload KML files if present
//       if (kmlFiles.length > 0) {
//         for (const file of kmlFiles) {
//           const formData = new FormData();
//           formData.append("file", file);
//           formData.append("upload_preset", "ml_default");

//           const response = await axios.post(
//             "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//             formData
//           );

//           uploadedKmlUrls.push(response.data.secure_url);
//         }
//       }

//       // Upload document if present
//       if (document) {
//         const formData = new FormData();
//         formData.append("file", document);
//         formData.append("upload_preset", "ml_default");

//         const response = await axios.post(
//           "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//           formData
//         );

//         documentUrl = response.data.secure_url;
//         console.log("Uploaded PDF URL:", documentUrl);
//       }




//       // Prepare data to submit
//       const payload = {
//         kmlUrls: uploadedKmlUrls.length > 0 ? uploadedKmlUrls : null,
//         documentUrl: documentUrl || null,
//       };

//       await handleSubmit(payload);

//       setMessage("Files uploaded and stored successfully!");
//     } catch (error) {
//       setMessage("Upload failed. Please try again.");
//       console.error("Upload Error:", error);
//     } finally {
//       setIsUploading(false);
//       setKmlFiles([]);
//     }
//   };



//   return (
//     <Box display="flex" justifyContent="center" alignItems="flex-start" p={2} ref={formRef}>
//       <Paper elevation={3} sx={{ p: 3, borderRadius: 4, maxWidth: 750, width: '100%', background: '#fff', mt: 0, position: 'relative' }}>
//         <button
//           className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//           onClick={onClose}
//         >
//           <X size={14} />
//         </button>

//         <Typography variant="h5" align="center" fontWeight="bold" color="primary" gutterBottom>
//           {editingProject ? "Edit Project" : "Add Project"}
//         </Typography>
//         <Divider sx={{ my: 2 }} />
//         <form onSubmit={(e) => handleKmlUpload(e)} autoComplete="off">
//           <Grid container spacing={2}>
//             {/* Project Name */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Project Name"
//                 fullWidth
//                 size="small"
//                 name="project_name"
//                 value={formData.project_name}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("project_name")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '&.animate-shake fieldset': {
//                       borderColor: 'red',
//                     },
//                   },
//                 }}
//               />
//             </Grid>

//             {/* Sector */}
//             <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
//               <TextField
//                 label="Sector"
//                 select
//                 fullWidth
//                 size="small"
//                 name="sector"
//                 value={formData.sector}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("sector")}
//                 SelectProps={{
//                   renderValue: (selected) => selected,
//                 }}
//               >
//                 <MenuItem value="">Select Sector</MenuItem>
//                 {sectors.map((sector, index) => (
//                   <MenuItem key={index} value={sector}>
//                     <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
//                       {sector}
//                       {formData.sector !== sector && (
//                         <IconButton
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleDeleteSector(sector);
//                           }}
//                           size="small"
//                         >
//                           <X size={14} />
//                         </IconButton>
//                       )}
//                     </Box>
//                   </MenuItem>
//                 ))}
//               </TextField>
            
//               <button
//                 className="absolute top-1 -right-3 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(true)}
//               >
//                 <Plus size={18} />
//               </button>
//             </Grid>

//             {/* Cost */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Cost (INR)"
//                 type="number"
//                 fullWidth
//                 size="small"
//                 name="cost"
//                 value={formData.cost}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("cost")}
//               />
//             </Grid>

//             {/* Status */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Status"
//                 select
//                 fullWidth
//                 size="small"
//                 name="status"
//                 value={formData.status}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("status")}
//               >
//                 <MenuItem value="">Select Status</MenuItem>
//                 <MenuItem value="Conceptual">Conceptual</MenuItem>
//                 <MenuItem value="DPR">DPR</MenuItem>
//                 <MenuItem value="Tender">Tender</MenuItem>
//                 <MenuItem value="Under-Construction">Under-Construction</MenuItem>
//                 <MenuItem value="Constructed">Constructed</MenuItem>
//               </TextField>
//             </Grid>

//             {/* Country */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Country"
//                 select
//                 fullWidth
//                 size="small"
//                 name="country"
//                 value={formData.country}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("country")}
//               >
//                 <MenuItem value="">Select Country</MenuItem>
//                 {Country.getAllCountries().map((country) => (
//                   <MenuItem key={country.isoCode} value={country.name}>
//                     {country.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* State */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="State"
//                 select
//                 fullWidth
//                 size="small"
//                 name="state"
//                 value={formData.state}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("state")}
//               >
//                 <MenuItem value="">Select State</MenuItem>
//                 {State.getStatesOfCountry(getCountryCode(formData.country)).map((state) => (
//                   <MenuItem key={state.isoCode} value={state.name}>
//                     {state.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* City */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="City"
//                 select
//                 fullWidth
//                 size="small"
//                 name="city"
//                 value={formData.city}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("city")}
//               >
//                 <MenuItem value="">Select City</MenuItem>
//                 {City.getCitiesOfState(getCountryCode(formData.country), getStateCode(formData.state, getCountryCode(formData.country))).map((city) => (
//                   <MenuItem key={city.name} value={city.name}>
//                     {city.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* Contractor */}
//             <Grid item xs={12} md={6}>
//               <TextField label="Contractor" fullWidth size="small" name="contractor" value={formData.contractor} onChange={handleChange} variant="outlined" />
//             </Grid>

//             {/* districtMagistrate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="District Magistrate"
//                 fullWidth
//                 size="small"
//                 name="districtMagistrate"
//                 value={formData.districtMagistrate}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("districtMagistrate")}
//               />
//             </Grid>

//             {/* population */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Population"
//                 fullWidth
//                 size="small"
//                 type="number"
//                 name="population"
//                 value={formData.population}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("population")}
//               />
//             </Grid>

//             {/* registrarOffice */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Registrar Office"
//                 fullWidth
//                 size="small"
//                 name="registrarOffice"
//                 value={formData.registrarOffice}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("registrarOffice")}
//               />
//             </Grid>

//             {/* circleRate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Circle Rate (INR)"
//                 fullWidth
//                 size="small"
//                 name="circleRate"
//                 type="number"
//                 value={formData.circleRate}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("circleRate")}
//               />
//             </Grid>

//             {/* Start Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Start Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="startDate"
//                 value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "startDate")}
//                 variant="outlined"
//                 className={shouldShake("start")}
//               />
//             </Grid>

//             {/* Finish Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Finish Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="endDate"
//                 value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "endDate")}
//                 variant="outlined"
//                 className={shouldShake("finish")}
//               />
//             </Grid>

//             {/* KML File Upload */}
//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{ py: 1, border: '1px dashed #3b82f6', color: '#3b82f6', fontWeight: 'bold', borderRadius: 2, position: 'relative', }}
//               >
//                 <MapPinned size={18} style={{ marginRight: '6px' }} />
//                 Upload KML
//                 <input type="file" name="kmlFile" accept=".kml, .kmz" multiple onChange={handleChange} disabled={isUploading} className="absolute inset-0 opacity-0 cursor-pointer" />
//               </Button>
//               {formData.kmlFile && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {formData.kmlFile.name}
//                 </Typography>
//               )}
//             </Grid>

//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{
//                   py: 1,
//                   border: '1px dashed #3b82f6',
//                   color: '#3b82f6',
//                   fontWeight: 'bold',
//                   borderRadius: 2,
//                   position: 'relative',
//                 }}
//               >
//                 <FilePlus size={18} style={{ marginRight: '6px' }} />
//                 Upload Document
//                 <input
//                   type="file"
//                   name="documentFile"
//                   onChange={handleChange}
//                   style={{ display: 'none' }}
//                   accept=".pdf,.doc,.docx"
//                 />
//               </Button>
//               {fileName && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {fileName}
//                 </Typography>
//               )}
//             </Grid>

//             {/* Submit Button */}
//             <Grid item xs={12}>
//               <Button
//                 type="submit"
//                 fullWidth
//                 variant="contained"
//                 sx={{ py: 1, background: 'linear-gradient(135deg, #3b82f6, #6d28d9)', color: '#fff', fontWeight: 'bold', borderRadius: 2 }}
//               >
//                 Submit
//               </Button>
//             </Grid>
//           </Grid>
//         </form>

//         {/* Add Sector Modal */}
//         {showModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
//             <div className="bg-white rounded-lg p-6 w-80 relative" onClick={(e) => e.stopPropagation()}>
//               <button
//                 className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(false)}
//               >
//                 <X size={14} />
//               </button>
//               <div className="flex flex-col items-center">
//                 <h2 className="text-xl font-bold mb-3">Add New Sector</h2>
//                 <form onSubmit={(e) => {
//                   e.preventDefault();
//                   handleAddSector();
//                 }} className="w-full">
//                   <TextField
//                     autoComplete="off"
//                     label="New Sector"
//                     fullWidth
//                     size="small"
//                     value={newSector}
//                     onChange={(e) => setNewSector(e.target.value)}
//                     variant="outlined"
//                     className={shouldShake("sector")}
//                     error={!!sectorError}
//                     helperText={sectorError}
//                     sx={{ mb: 2 }}
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         e.preventDefault();
//                         handleAddSector();
//                       }
//                     }}
//                   />
//                   <div className="flex justify-center w-full">
//                     <Button
//                       type="submit"
//                       variant="contained"
//                       sx={{
//                         py: 1,
//                         background: 'linear-gradient(135deg, #3b82f6, #6d28d9)',
//                         color: '#fff',
//                         fontWeight: 'bold',
//                         borderRadius: 2,
//                         width: '100%',
//                         maxWidth: '120px'
//                       }}
//                       onClick={handleAddSector}
//                     >
//                       Add
//                     </Button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Success Modal */}
//         {showSuccessModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-xl shadow-md p-6 w-80 relative">
//               <button onClick={closeSuccessModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Congratulations!</h2>
//                 <p className="text-gray-600 text-center text-sm">
//                   Your project has been successfully submitted.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Update Modal */}
//         {showUpdateModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 w-80 relative">
//               <button onClick={closeUpdateModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <Edit className="w-10 h-10 text-blue-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Successfully Updated!</h2>
//                 <p className="text-gray-600 text-center text-sm">Your project has been successfully updated.</p>
//               </div>
//             </div>
//           </div>
//         )}
//       </Paper>
//     </Box>
//   );
// };

// export default AddProject1;


// import React, { useState, useEffect, useRef } from "react";
// import axios from 'axios';
// import "react-datepicker/dist/react-datepicker.css";
// import { Country, State, City } from "country-state-city";
// import { Plus, Upload, X, CheckCircle, Trash, Edit, FilePlus, MapPinned } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import { TextField, MenuItem, Button, Box, Typography, Grid, Paper, Divider, IconButton, InputAdornment } from '@mui/material';
// import axiosInstance from "../axiosInstance";

// const AddProject1 = ({ editingProject, onClose }) => {
//   const [formData, setFormData] = useState({
//     project_name: "",
//     sector: "",
//     cost: "",
//     status: "",
//     country: "",
//     state: "",
//     city: "",
//     startDate: null,
//     endDate: null,
//     contractor: "",
//     districtMagistrate: '',
//     population: '',
//     registrarOffice: '',
//     circleRate: '',
//     kmlFile: null,
//     documentFile: null,
//   });

//   const [sectors, setSectors] = useState(["Commercial", "Construction", "Education", "Factory", "Highways", "Infrastructure", "Mall", "Railways", "Residential", "Retail"]);

//   const [newSector, setNewSector] = useState("");
//   const [showModal, setShowModal] = useState(false);
//   const [showSuccessModal, setShowSuccessModal] = useState(false);
//   const [showUpdateModal, setShowUpdateModal] = useState(false);
//   const [shakeFields, setShakeFields] = useState([]);
//   const [sectorError, setSectorError] = useState("");
//   const [fileName, setFileName] = useState('');
//   const [kmlFileName, setKmlFileName] = useState(''); // New state for KML file name
//   const [message, setMessage] = useState("");
//   const [isUploading, setIsUploading] = useState(false);
//   const [kmlFiles, setKmlFiles] = useState([]);

//   const companyId = '67ce66294f28f85e4ff91e2c';
//   const navigate = useNavigate();
//   const formRef = useRef(null);

//   useEffect(() => {
//     if (editingProject) {
//       setFormData({
//         ...editingProject,
//         startDate: editingProject.startDate ? new Date(editingProject.startDate) : null,
//         endDate: editingProject.endDate ? new Date(editingProject.endDate) : null,
//       });
//       if (editingProject.kmlFile) {
//         setKmlFileName(editingProject.kmlFile.name || 'KML file uploaded');
//       }
//     }
//   }, [editingProject]);

//   const handleAddSector = () => {
//     if (newSector.trim() && !sectors.includes(newSector.trim())) {
//       setSectors((prev) => [...prev, newSector.trim()]);
//       setNewSector("");
//       setShowModal(false);
//       setSectorError("");
//     } else {
//       setSectorError("Sector already exists or invalid input!");
//       setShakeFields(["sector"]);
//     }
//   };

//   const handleDeleteSector = (sectorToDelete) => {
//     setSectors((prev) => prev.filter((sector) => sector !== sectorToDelete));
//     if (formData.sector === sectorToDelete) {
//       setFormData({ ...formData, sector: "" });
//     }
//   };

//   const getCountryCode = (countryName) => {
//     const country = Country.getAllCountries().find((c) => c.name === countryName);
//     return country ? country.isoCode : "";
//   };

//   const getStateCode = (stateName, countryCode) => {
//     const state = State.getStatesOfCountry(countryCode).find((s) => s.name === stateName);
//     return state ? state.isoCode : "";
//   };

//   const [document, setDocument] = useState("");

//   const handleChange = (e) => {
//     const { name, value, files } = e.target;
//     if (name === "kmlFile") {
//       const selectedFiles = Array.from(files);
//       setKmlFiles(selectedFiles);
//       setFormData({
//         ...formData,
//         [name]: selectedFiles[0],
//       });
//       // Set KML file name for display
//       if (selectedFiles.length > 0) {
//         setKmlFileName(
//           selectedFiles.length > 1
//             ? `${selectedFiles.length} files selected`
//             : selectedFiles[0].name
//         );
//       } else {
//         setKmlFileName('');
//       }
//     } else if (name === "documentFile") {
//       const file = files[0];
//       setDocument(file)
//       setFormData({
//         ...formData,
//         [name]: file,
//       });
//       setFileName(file ? file.name : '');
//       // console.log(fileName)
//     } else if (name === "country") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         state: "",
//         city: "",
//       });
//     } else if (name === "state") {
//       setFormData({
//         ...formData,
//         [name]: value,
//         city: "",
//       });
//     } else {
//       setFormData({
//         ...formData,
//         [name]: value,
//       });
//     }
//   };

//   const handleDateChange = (date, field) => {
//     setFormData({
//       ...formData,
//       [field]: date,
//     });
//   };

//   const validateForm = () => {
//     const requiredFields = [
//       'project_name',
//       'sector',
//       'cost',
//       'status',
//       'country',
//       'state',
//       'city'
//     ];
    
//     const emptyFields = requiredFields.filter(field => !formData[field]);
    
//     // Check if both kmlFile and documentFile are empty
//     if (!formData.kmlFile && !formData.documentFile) {
//       emptyFields.push('kmlFile');
//       emptyFields.push('documentFile');
//     }
    
//     if (emptyFields.length > 0) {
//       setShakeFields(emptyFields);
//       setTimeout(() => setShakeFields([]), 500);
//       return false;
//     }
    
//     return true;
//   };

//   const handleSubmit = async (payloadurl) => {
//     if (!validateForm()) {
//       return;
//     }
    
//     console.log("upload kml s", JSON.stringify(formData, null, " "))
//     console.log("upload kml s", JSON.stringify(payloadurl, null, " "))
//     // return ;
//     try {
//       const payload = {
//         project_name: formData.project_name,
//         sector: formData.sector,
//         cost: formData.cost,
//         status: formData.status,
//         country: formData.country,
//         state: formData.state,
//         city: formData.city,
//         startDate: formData.startDate?.toISOString().split("T")[0],
//         endDate: formData.endDate?.toISOString().split("T")[0],
//         contractor: formData.contractor,
//         districtMagistrate: formData.districtMagistrate,
//         population: formData.population,
//         registrarOffice: formData.registrarOffice,
//         circleRate: formData.circleRate,
//         kml: payloadurl.kmlUrls ? payloadurl.kmlUrls : [],
//         documentFile: payloadurl.documentUrl ? payloadurl.documentUrl : [],
//       };

//       if (editingProject) {
//         const response = await axiosInstance.put(
//           `/project/updateProject`,
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'Content-Type': 'application/json',
//               'x-project-id': editingProject._id
//             },
//           }
//         );
//         console.log('Project updated successfully:', response.data);
//         setShowUpdateModal(true);
//       } else {
//         const response = await axiosInstance.post(
//           '/project/createProject',
//           payload,
//           {
//             headers: {
//               'x-company-id': companyId,
//               'x-auth-token':localStorage.getItem('token'),
//               'Content-Type': 'application/json',
//             },
//           }
//         );
//         console.log('Project created successfully:', response.data);
//         setShowSuccessModal(true);
//       }
//     } catch (error) {
//       console.error('Error submitting project:', error.response?.data || error.message);
//     }
//   };

//   const closeSuccessModal = () => {
//     setShowSuccessModal(false);
//     onClose();
//   };

//   const closeUpdateModal = () => {
//     setShowUpdateModal(false);
//     onClose();
//   };

//   const shouldShake = (fieldName) => {
//     return shakeFields.includes(fieldName) ? "animate-shake" : "";
//   };

//   const handleKmlUpload = async (e) => {
//     e.preventDefault();
    
//     if (!validateForm()) {
//       return;
//     }
    
//     console.log(`KML files: ${kmlFiles}`);
//     console.log(`Document file: ${document?.name || "No document uploaded"}`);

//     if (kmlFiles.length === 0 && !document) {
//       setMessage("No files selected!");
//       console.log("No files uploaded");
//       await handleSubmit({});
//       return;
//     }

//     setIsUploading(true);
//     setMessage("Uploading files...");

//     try {
//       let uploadedKmlUrls = [];
//       let documentUrl = null;

//       // Upload KML files if present
//       if (kmlFiles.length > 0) {
//         for (const file of kmlFiles) {
//           const formData = new FormData();
//           formData.append("file", file);
//           formData.append("upload_preset", "ml_default");

//           const response = await axios.post(
//             "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//             formData
//           );

//           uploadedKmlUrls.push(response.data.secure_url);
//         }
//       }

//       // Upload document if present
//       if (document) {
//         const formData = new FormData();
//         formData.append("file", document);
//         formData.append("upload_preset", "ml_default");

//         const response = await axios.post(
//           "https://api.cloudinary.com/v1_1/dnqrumfwn/upload",
//           formData
//         );

//         documentUrl = response.data.secure_url;
//         console.log("Uploaded PDF URL:", documentUrl);
//       }

//       // Prepare data to submit
//       const payload = {
//         kmlUrls: uploadedKmlUrls.length > 0 ? uploadedKmlUrls : null,
//         documentUrl: documentUrl || null,
//       };

//       await handleSubmit(payload);

//       setMessage("Files uploaded and stored successfully!");
//     } catch (error) {
//       setMessage("Upload failed. Please try again.");
//       console.error("Upload Error:", error);
//     } finally {
//       setIsUploading(false);
//       setKmlFiles([]);
//     }
//   };

//   return (
//     <Box display="flex" justifyContent="center" alignItems="flex-start" p={2} ref={formRef}>
//       <Paper elevation={3} sx={{ p: 3, borderRadius: 4, maxWidth: 750, width: '100%', background: '#fff', mt: 0, position: 'relative' }}>
//         <button
//           className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//           onClick={onClose}
//         >
//           <X size={14} />
//         </button>

//         <Typography variant="h5" align="center" fontWeight="bold" color="primary" gutterBottom>
//           {editingProject ? "Edit Project" : "Add Project"}
//         </Typography>
//         <Divider sx={{ my: 2 }} />
//         <form onSubmit={(e) => handleKmlUpload(e)} autoComplete="off">
//           <Grid container spacing={2}>
//             {/* Project Name */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Project Name"
//                 fullWidth
//                 size="small"
//                 name="project_name"
//                 value={formData.project_name}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("project_name")}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     '&.animate-shake fieldset': {
//                       borderColor: 'red',
//                     },
//                   },
//                 }}
//                 error={shakeFields.includes("project_name")}
//                 helperText={shakeFields.includes("project_name") ? "This field is required" : ""}
//               />
//             </Grid>

//             {/* Sector */}
//             <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
//               <TextField
//                 label="Sector"
//                 select
//                 fullWidth
//                 size="small"
//                 name="sector"
//                 value={formData.sector}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("sector")}
//                 SelectProps={{
//                   renderValue: (selected) => selected,
//                 }}
//                 error={shakeFields.includes("sector")}
//                 helperText={shakeFields.includes("sector") ? "This field is required" : ""}
//               >
//                 <MenuItem value="">Select Sector</MenuItem>
//                 {sectors.map((sector, index) => (
//                   <MenuItem key={index} value={sector}>
//                     <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
//                       {sector}
//                       {formData.sector !== sector && (
//                         <IconButton
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleDeleteSector(sector);
//                           }}
//                           size="small"
//                         >
//                           <X size={14} />
//                         </IconButton>
//                       )}
//                     </Box>
//                   </MenuItem>
//                 ))}
//               </TextField>
            
//               <button
//                 className="absolute top-1 -right-3 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(true)}
//               >
//                 <Plus size={18} />
//               </button>
//             </Grid>

//             {/* Cost */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Cost (INR)"
//                 type="number"
//                 fullWidth
//                 size="small"
//                 name="cost"
//                 value={formData.cost}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("cost")}
//                 error={shakeFields.includes("cost")}
//                 helperText={shakeFields.includes("cost") ? "This field is required" : ""}
//               />
//             </Grid>

//             {/* Status */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Status"
//                 select
//                 fullWidth
//                 size="small"
//                 name="status"
//                 value={formData.status}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("status")}
//                 error={shakeFields.includes("status")}
//                 helperText={shakeFields.includes("status") ? "This field is required" : ""}
//               >
//                 <MenuItem value="">Select Status</MenuItem>
//                 <MenuItem value="Conceptual">Conceptual</MenuItem>
//                 <MenuItem value="DPR">DPR</MenuItem>
//                 <MenuItem value="Tender">Tender</MenuItem>
//                 <MenuItem value="Under-Construction">Under-Construction</MenuItem>
//                 <MenuItem value="Constructed">Constructed</MenuItem>
//               </TextField>
//             </Grid>

//             {/* Country */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Country"
//                 select
//                 fullWidth
//                 size="small"
//                 name="country"
//                 value={formData.country}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("country")}
//                 error={shakeFields.includes("country")}
//                 helperText={shakeFields.includes("country") ? "This field is required" : ""}
//               >
//                 <MenuItem value="">Select Country</MenuItem>
//                 {Country.getAllCountries().map((country) => (
//                   <MenuItem key={country.isoCode} value={country.name}>
//                     {country.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* State */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="State"
//                 select
//                 fullWidth
//                 size="small"
//                 name="state"
//                 value={formData.state}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("state")}
//                 error={shakeFields.includes("state")}
//                 helperText={shakeFields.includes("state") ? "This field is required" : ""}
//               >
//                 <MenuItem value="">Select State</MenuItem>
//                 {State.getStatesOfCountry(getCountryCode(formData.country)).map((state) => (
//                   <MenuItem key={state.isoCode} value={state.name}>
//                     {state.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* City */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="City"
//                 select
//                 fullWidth
//                 size="small"
//                 name="city"
//                 value={formData.city}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("city")}
//                 error={shakeFields.includes("city")}
//                 helperText={shakeFields.includes("city") ? "This field is required" : ""}
//               >
//                 <MenuItem value="">Select City</MenuItem>
//                 {City.getCitiesOfState(getCountryCode(formData.country), getStateCode(formData.state, getCountryCode(formData.country))).map((city) => (
//                   <MenuItem key={city.name} value={city.name}>
//                     {city.name}
//                   </MenuItem>
//                 ))}
//               </TextField>
//             </Grid>

//             {/* Contractor */}
//             <Grid item xs={12} md={6}>
//               <TextField label="Contractor" fullWidth size="small" name="contractor" value={formData.contractor} onChange={handleChange} variant="outlined" />
//             </Grid>

//             {/* districtMagistrate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="District Magistrate"
//                 fullWidth
//                 size="small"
//                 name="districtMagistrate"
//                 value={formData.districtMagistrate}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("districtMagistrate")}
//               />
//             </Grid>

//             {/* population */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Population"
//                 fullWidth
//                 size="small"
//                 type="number"
//                 name="population"
//                 value={formData.population}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("population")}
//               />
//             </Grid>

//             {/* registrarOffice */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Registrar Office"
//                 fullWidth
//                 size="small"
//                 name="registrarOffice"
//                 value={formData.registrarOffice}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("registrarOffice")}
//               />
//             </Grid>

//             {/* circleRate */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Circle Rate (INR)"
//                 fullWidth
//                 size="small"
//                 name="circleRate"
//                 type="number"
//                 value={formData.circleRate}
//                 onChange={handleChange}
//                 variant="outlined"
//                 className={shouldShake("circleRate")}
//               />
//             </Grid>

//             {/* Start Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Start Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="startDate"
//                 value={formData.startDate ? formData.startDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "startDate")}
//                 variant="outlined"
//                 className={shouldShake("start")}
//               />
//             </Grid>

//             {/* Finish Date */}
//             <Grid item xs={12} md={6}>
//               <TextField
//                 label="Finish Date"
//                 type="date"
//                 fullWidth
//                 size="small"
//                 InputLabelProps={{ shrink: true }}
//                 name="endDate"
//                 value={formData.endDate ? formData.endDate.toISOString().split('T')[0] : ""}
//                 onChange={(e) => handleDateChange(new Date(e.target.value), "endDate")}
//                 variant="outlined"
//                 className={shouldShake("finish")}
//               />
//             </Grid>

//             {/* KML File Upload */}
//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{ 
//                   py: 1, 
//                   border: shakeFields.includes("kmlFile") && shakeFields.includes("documentFile") 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6', 
//                   color: shakeFields.includes("kmlFile") && shakeFields.includes("documentFile") 
//                     ? 'red' 
//                     : '#3b82f6', 
//                   fontWeight: 'bold', 
//                   borderRadius: 2, 
//                   position: 'relative', 
//                 }}
//               >
//                 <MapPinned size={18} style={{ marginRight: '6px' }} />
//                 Upload KML
//                 <input type="file" name="kmlFile" accept=".kml, .kmz" multiple onChange={handleChange} disabled={isUploading} className="absolute inset-0 opacity-0 cursor-pointer" />
//               </Button>
//               {shakeFields.includes("kmlFile") && shakeFields.includes("documentFile") && (
//                 <Typography sx={{ color: 'red', textAlign: 'center', mt: 1 }}>
//                   Please upload at least one file (KML or Document)
//                 </Typography>
//               )}
//               {formData.kmlFile && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {formData.kmlFile.name}
//                 </Typography>
//               )}
//             </Grid>

//             <Grid item xs={12}>
//               <Button
//                 component="label"
//                 fullWidth
//                 variant="outlined"
//                 sx={{
//                   py: 1,
//                   border: shakeFields.includes("kmlFile") && shakeFields.includes("documentFile") 
//                     ? '1px dashed red' 
//                     : '1px dashed #3b82f6',
//                   color: shakeFields.includes("kmlFile") && shakeFields.includes("documentFile") 
//                     ? 'red' 
//                     : '#3b82f6',
//                   fontWeight: 'bold',
//                   borderRadius: 2,
//                   position: 'relative',
//                 }}
//               >
//                 <FilePlus size={18} style={{ marginRight: '6px' }} />
//                 Upload Document
//                 <input
//                   type="file"
//                   name="documentFile"
//                   onChange={handleChange}
//                   style={{ display: 'none' }}
//                   accept=".pdf,.doc,.docx"
//                 />
//               </Button>
//               {fileName && (
//                 <Typography sx={{ color: '#3b82f6', fontWeight: 'medium', textAlign: 'center', mt: 1 }}>
//                   {fileName}
//                 </Typography>
//               )}
//             </Grid>

//             {/* Submit Button */}
//             <Grid item xs={12}>
//               <Button
//                 type="submit"
//                 fullWidth
//                 variant="contained"
//                 sx={{ py: 1, background: 'linear-gradient(135deg, #3b82f6, #6d28d9)', color: '#fff', fontWeight: 'bold', borderRadius: 2 }}
//               >
//                 Submit
//               </Button>
//             </Grid>
//           </Grid>
//         </form>

//         {/* Add Sector Modal */}
//         {showModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
//             <div className="bg-white rounded-lg p-6 w-80 relative" onClick={(e) => e.stopPropagation()}>
//               <button
//                 className="absolute -top-2 -right-2 bg-white w-6 h-6 border border-gray-300 shadow-sm flex items-center justify-center rounded-full"
//                 onClick={() => setShowModal(false)}
//               >
//                 <X size={14} />
//               </button>
//               <div className="flex flex-col items-center">
//                 <h2 className="text-xl font-bold mb-3">Add New Sector</h2>
//                 <form onSubmit={(e) => {
//                   e.preventDefault();
//                   handleAddSector();
//                 }} className="w-full">
//                   <TextField
//                     autoComplete="off"
//                     label="New Sector"
//                     fullWidth
//                     size="small"
//                     value={newSector}
//                     onChange={(e) => setNewSector(e.target.value)}
//                     variant="outlined"
//                     className={shouldShake("sector")}
//                     error={!!sectorError}
//                     helperText={sectorError}
//                     sx={{ mb: 2 }}
//                     onKeyDown={(e) => {
//                       if (e.key === 'Enter') {
//                         e.preventDefault();
//                         handleAddSector();
//                       }
//                     }}
//                   />
//                   <div className="flex justify-center w-full">
//                     <Button
//                       type="submit"
//                       variant="contained"
//                       sx={{
//                         py: 1,
//                         background: 'linear-gradient(135deg, #3b82f6, #6d28d9)',
//                         color: '#fff',
//                         fontWeight: 'bold',
//                         borderRadius: 2,
//                         width: '100%',
//                         maxWidth: '120px'
//                       }}
//                       onClick={handleAddSector}
//                     >
//                       Add
//                     </Button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Success Modal */}
//         {showSuccessModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-xl shadow-md p-6 w-80 relative">
//               <button onClick={closeSuccessModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Congratulations!</h2>
//                 <p className="text-gray-600 text-center text-sm">
//                   Your project has been successfully submitted.
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Update Modal */}
//         {showUpdateModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
//             <div className="bg-white rounded-lg p-6 w-80 relative">
//               <button onClick={closeUpdateModal} className="absolute top-1 right-1 p-1 hover:bg-gray-100 rounded-full">
//                 <X className="w-5 h-5" />
//               </button>
//               <div className="flex flex-col items-center">
//                 <Edit className="w-10 h-10 text-blue-500 mb-3" />
//                 <h2 className="text-xl font-bold mb-1">Successfully Updated!</h2>
//                 <p className="text-gray-600 text-center text-sm">Your project has been successfully updated.</p>
//               </div>
//             </div>
//           </div>
//         )}
//       </Paper>
//     </Box>
//   );
// };

// export default AddProject1;